import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Box, Button, Typography, Tabs, Tab, CircularProgress, Alert, Slider, Select, MenuItem, FormControl, InputLabel, TextField, ToggleButton, ToggleButtonGroup, Autocomplete } from '@mui/material';
import { chartDataService } from '../services/chartDataService';
import { youtubeService } from '../services/youtubeService';
import { SERVER_BASE_URL, MASTER_VIDEO_LIST_FALLBACK_ID, getVoLanguage } from '../config';
import { useThemeColors } from '../theme/useThemeColors';
import { useGoogleSheets } from '../context/GoogleSheetsContext';
// --- Import MiniVideoPlayer --- 
import { MiniVideoPlayer } from './MiniVideoPlayer';
// --- Import VideoSelectionDialog --- 
import { VideoSelectionDialog } from './VideoSelectionDialog';
// These imports can be removed since we're not using them anymore
// import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
// import { ResizableBox } from 'react-resizable';
// Import styles needed for react-resizable
// import 'react-resizable/css/styles.css';
// This import can be removed
// import * as ReactDOM from 'react-dom';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'; // Remove unused icon
import { SelectChangeEvent } from '@mui/material/Select'; // Import correct type for Select change
// Remove toastify imports entirely
// import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { ToastContainer } from 'react-toastify';

// --- Constants ---
// Settings live in the chart_weeks.settings JSONB (key→value), accessed via the
// typed chartDataService (getSettings / setSetting). The legacy A1-cell arrays
// (INTRO/OUTRO/TEASE_SETTINGS_CELLS) and MASTER_VIDEO_LIST_RANGE are gone — every
// data access is now a typed domain method, no spreadsheet ranges.
// Remove unused constant
// const LOCAL_STORAGE_LAYOUT_KEY = 'introOutroLayoutConfig';

// Helper to parse string value to number, removing "px" or "%"
const parsePixels = (value: string | number | undefined): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value.replace(/px|%/, ''));
  return 0; // Default to 0 if undefined or not parseable
};

// --- Define VideoFormat type at the top level ---
type VideoFormat = 'horizontal' | 'vertical';

// Helper to map a slotId to its chart_weeks.settings KEYS (no A1 notation).
// These are the canonical settings keys stored in the DB settings JSONB and read
// by the server export (exportProcessor.js settingsMap.get(...)) and validation
// (OutputManager.tsx key→row map). They are written via chartDataService.setSetting
// and read via chartDataService.getSettings().settings[key].
//   carousel_video{1-4}: url=carousel_video{n}_url, seek=carousel_video{n}_seek (was B17/B18..)
//   outro_video{1-6}:    url=outro_video{n}_url, seek=outro_video{n}_seek         (was B37/B38..)
//   video_left:          url=video_left_url, seek=video_left_seek                 (was B25/B26)
//   tease_shot{1-4}:     url/seek/duration/h_offset/v_offset                      (was B64..B83)
//   tease_voiceover:     tease_intro_voiceover_url                                (was B63)
// Language-scoped tease settings keys: EN keeps the legacy unsuffixed keys, other
// languages get a `_<lang>` suffix (mirrors the /es/ VO S3 segment convention) so
// bilingual charts keep a separate tease per language.
const teaseSettingKey = (base: string, lang: string): string => lang === 'en' ? base : `${base}_${lang}`;
const getKeysForSlot = (slotId: string, lang: string = 'en'): { urlKey: string, seekKey?: string, durationKey?: string, hOffsetKey?: string, vOffsetKey?: string } | null => {
  if (slotId.startsWith('carousel_video')) {
    const num = parseInt(slotId.replace('carousel_video', ''), 10);
    if (num >= 1 && num <= 4) {
      return { urlKey: `carousel_video${num}_url`, seekKey: `carousel_video${num}_seek` };
    }
  } else if (slotId.startsWith('outro_video')) {
    const num = parseInt(slotId.replace('outro_video', ''), 10);
    if (num >= 1 && num <= 6) {
      return { urlKey: `outro_video${num}_url`, seekKey: `outro_video${num}_seek` };
    }
  } else if (slotId === 'video_left') {
    return { urlKey: 'video_left_url', seekKey: 'video_left_seek' };
  } else if (slotId.startsWith('tease_shot')) {
    const num = parseInt(slotId.replace('tease_shot', ''), 10);
    if (num >= 1 && num <= 4) {
      return {
        urlKey: `tease_shot${num}_url`,
        seekKey: `tease_shot${num}_seek`,
        durationKey: `tease_shot${num}_duration`,
        hOffsetKey: `tease_shot${num}_h_offset`,
        vOffsetKey: `tease_shot${num}_v_offset`
      };
    }
  } else if (slotId === 'tease_voiceover') {
    return { urlKey: teaseSettingKey('tease_intro_voiceover_url', lang) }; // Tease VO URL only
  }
  return null;
};

interface IntroSettings {
  [key: string]: string;
}

interface OutroSettings {
  [key: string]: string;
}

interface TeaseSettings {
  [key: string]: string;
}

interface MasterVideoInfo {
  song: string;
  artist: string;
  s3Url: string;
  seekIn: string; // Store as string initially
  position?: number; // Add position info
}

// --- Layout Config Interface ---
interface PlayerLayout {
  top: string;    // Store as percentage string e.g., "10%"
  left: string;   // Store as percentage string e.g., "5%"
  width: string;  // Store as pixel string e.g., "150px"
  height: string; // Store as pixel string e.g., "84px"
}
interface LayoutConfig {
  [slotId: string]: PlayerLayout;
}

// --- Default Layout (Now the only layout) ---
const HORIZONTAL_DEFAULT_LAYOUT: LayoutConfig = {
  // Intro Defaults - Increased height by another 30px
  'carousel_video1': { top: '34.59982110939785%', left: '48.0%', width: '130.8671875px', height: '176.056640625px' },
  'carousel_video2': { top: '34.59982110939785%', left: '60.8%', width: '130.8671875px', height: '176.056640625px' },
  'carousel_video3': { top: '34.59982110939785%', left: '73.6%', width: '130.8671875px', height: '176.056640625px' },
  'carousel_video4': { top: '34.59982110939785%', left: '86.4%', width: '130.8671875px', height: '176.056640625px' },
  'video_left': { top: '3.8410472008534895%', left: '1.1618348449328058%', width: '491.142578125px', height: '580.45703125px' },
  'intro_shot': { top: '25%', left: '75%', width: '200px', height: '150px' }, // Positioned in upper right area
  // Outro Defaults
  'outro_video1': { top: '0%', left: '0%', width: '151.732421875px', height: '143.00390625px' },
  'outro_video2': { top: '0.2271223534201954%', left: '59.12560036656623%', width: '95.58984375px', height: '97.19921875px' },
  'outro_video3': { top: '0%', left: '68.23107798165137%', width: '366.97265625px', height: '247.720703125px' },
  'outro_video4': { top: '45.04690384072552%', left: '0.19196181516325403%', width: '316.455078125px', height: '353.65625px' },
  'outro_video5': { top: '44.90592415588075%', left: '29.48601109408872%', width: '133.8984375px', height: '151.47265625px' },
  'outro_video6': { top: '38.09929763843646%', left: '82.25559059633022%', width: '192.87890625px', height: '381.23828125px' },
  // Tease shot defaults (4 shots in quad split - 2x2 grid, 16:9 aspect ratio, touching at center)
  'tease_shot1': { top: '0%', left: '0%', width: '50%', height: '50%' }, // Top left, touching center
  'tease_shot2': { top: '0%', left: '50%', width: '50%', height: '50%' }, // Top right, touching center
  'tease_shot3': { top: '50%', left: '0%', width: '50%', height: '50%' }, // Bottom left, touching center
  'tease_shot4': { top: '50%', left: '50%', width: '50%', height: '50%' }, // Bottom right, touching center
};

// --- Placeholder Vertical Default Layout (Update with actual values) ---
const VERTICAL_DEFAULT_LAYOUT: LayoutConfig = {
  // Intro Defaults - Placeholder, copy of horizontal for now
  'carousel_video1': { top: '53%', left: '10%', width: '97px', height: '145px' }, // Example, adjust
  'carousel_video2': { top: '53%', left: '30%', width: '97px', height: '145px' }, // Example, adjust
  'carousel_video3': { top: '53%', left: '50%', width: '97px', height: '145px' }, // Example, adjust
  'carousel_video4': { top: '53%', left: '70%', width: '97px', height: '145px' }, // Aligned with other carousel videos
  'video_left': { top: '1%', left: '20.2%', width: '320px', height: '180px' },        // Example, adjust
  // Outro Defaults - Placeholder, copy of horizontal for now (5 videos)
  'outro_video1': { top: '0%', left: '0%', width: '270px', height: '270px' },   // 270px x 270px
  'outro_video3': { top: '15%', left: '72%', width: '150px', height: '106px' },  // Width changed to 150px
  'outro_video4': { top: '37%', left: '0%', width: '149px', height: '127px' }, // Scaled up 5% more and moved up 2%
  'outro_video5': { top: '50%', left: '28%', width: '140px', height: '130px' }, // Back to 50%
  'outro_video6': { top: '50%', left: '53%', width: '315px', height: '540px' }, // Moved down 5% to 50%
  // Tease shot defaults (vertical layout, quad split - 2x2 grid, 9:16 aspect ratio)
  'tease_shot1': { top: '0%', left: '0%', width: '50%', height: '50%' }, // Top left, touching center
  'tease_shot2': { top: '0%', left: '50%', width: '50%', height: '50%' }, // Top right, touching center
  'tease_shot3': { top: '50%', left: '0%', width: '50%', height: '50%' }, // Bottom left, touching center
  'tease_shot4': { top: '50%', left: '50%', width: '50%', height: '50%' }, // Bottom right, touching center
};

// Add the setChartDate prop to component props
interface IntroOutroPageProps {
  setChartDate?: React.Dispatch<React.SetStateAction<string>>;
}

export const IntroOutroPage: React.FC<IntroOutroPageProps> = ({ setChartDate }) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // Get Master Video List ID from context (single source of truth)
  const { masterVideoListId: contextMasterVideoListId, currentChartConfig } = useGoogleSheets();
  const { themeColor, themeHoverColor } = useThemeColors();
  const MASTER_VIDEO_LIST_ID = contextMasterVideoListId || MASTER_VIDEO_LIST_FALLBACK_ID;

  // Get weeklySheetId from URL params (now using weekly sheet directly)
  const weeklySheetIdFromParams = params.renderSheetId; // URL param name unchanged for compatibility
  const weeklySheetId = weeklySheetIdFromParams;
  
  // Add debug logging
  console.log('[IntroOutro] URL params weeklySheetId:', weeklySheetIdFromParams);
  console.log('[IntroOutro] Using weeklySheetId (no render sheet):', weeklySheetId);

  // Active VO language for this sheet (bilingual charts: EN + ES). Scopes which
  // vo_lines the page reads and which tease settings keys/S3 paths it uses.
  const activeVoLang = getVoLanguage(weeklySheetId);
  
  // Set chart date from location state immediately if available
  const formattedDateFromState = location.state?.formattedDate;
  useEffect(() => {
    if (formattedDateFromState && setChartDate) {
      console.log(`[IntroOutro] Setting chart date from navigation state: ${formattedDateFromState}`);
      setChartDate(formattedDateFromState);
    }
  }, [formattedDateFromState, setChartDate]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [masterVideoMap, setMasterVideoMap] = useState<Map<string, MasterVideoInfo>>(new Map());
  const [currentWeekVideos, setCurrentWeekVideos] = useState<MasterVideoInfo[]>([]);
  const [cleanupNotification, setCleanupNotification] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  
  // Add missing state variables
  // --- Revert introSettings and outroSettings state to simple objects ---
  const [introSettings, setIntroSettings] = useState<IntroSettings>({});
  const [outroSettings, setOutroSettings] = useState<OutroSettings>({});
  const [teaseSettings, setTeaseSettings] = useState<TeaseSettings>({});
  const [hasTeaseText, setHasTeaseText] = useState<boolean>(false);
  const [youtubeImportUrl, setYoutubeImportUrl] = useState<{[key: string]: string}>({});
  const [generatedTeaseUrls, setGeneratedTeaseUrls] = useState<{
    vertical?: string;
    horizontal?: string;
  }>({});
  const [isGeneratingTeaseVO, setIsGeneratingTeaseVO] = useState<boolean>(false);
  const [isGeneratingTeaseMovie, setIsGeneratingTeaseMovie] = useState<boolean>(false);
  const [processedAudioUrl, setProcessedAudioUrl] = useState<string | null>(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState<boolean>(false);
  const [showRedProgressBars, setShowRedProgressBars] = useState<boolean>(false);
  const [previewProgress, setPreviewProgress] = useState<number>(0);
  const [previewDuration, setPreviewDuration] = useState<number>(10); // Default to 10s for tease
  const [previewAudioRef, setPreviewAudioRef] = useState<HTMLAudioElement | null>(null);
  const previewTimeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const previewIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [teaseVoDuration, setTeaseVoDuration] = useState<number | null>(null);
  const [teaseGainOverride, setTeaseGainOverride] = useState<string | null>(null); // P15 value (null = no override)
  const [globalGain, setGlobalGain] = useState<string>('0'); // P14 value
  const [isEditingTeaseGain, setIsEditingTeaseGain] = useState<boolean>(false);
  const [teaseGainInputValue, setTeaseGainInputValue] = useState<string>('');

  // Create a ref to hold the latest settings to avoid stale state in callbacks
  const settingsRef = useRef({ introSettings, outroSettings, teaseSettings });
  useEffect(() => {
    settingsRef.current = { introSettings, outroSettings, teaseSettings };
  }, [introSettings, outroSettings, teaseSettings]);

  // Fetch the tease VO in/out points, matching VOManager's precedence:
  // prefer the Line VO In/Out override (T15/U15) when both are present,
  // otherwise fall back to the main VO In/Out (N15/O15). VOManager writes
  // waveform-editor edits to T15/U15, so the tease must read them too or it
  // would use the stale N15/O15 value and clip the start of the VO.
  const fetchTeaseTiming = useCallback(async (
    fallbackIn: number,
    fallbackOut: number
  ): Promise<{ voIn: number; voOut: number; source: string }> => {
    // The VO data API resolves the tease in/out points using the SAME precedence
    // this used to do by hand (prefer the Line VO In/Out override — was T15/U15 —
    // when set, else the main VO In/Out — was N15/O15). voInPoints/voOutPoints
    // already carry the resolved value for the 'Tease' section, so read those and
    // fall back to the caller's defaults when absent.
    const vo = await chartDataService.getVO(weeklySheetId!, getVoLanguage(weeklySheetId));
    const inRaw = vo.voInPoints?.['Tease'];
    const outRaw = vo.voOutPoints?.['Tease'];
    const hasIn = inRaw !== undefined && String(inRaw).trim() !== '';
    const hasOut = outRaw !== undefined && String(outRaw).trim() !== '';
    return {
      voIn: hasIn ? parseFloat(String(inRaw)) : fallbackIn,
      voOut: hasOut ? parseFloat(String(outRaw)) : fallbackOut,
      source: (hasIn || hasOut) ? 'Tease VO In/Out (DB, line override preferred)' : 'fallback defaults',
    };
  }, [weeklySheetId]);

  // Function to calculate tease VO duration from the tease in/out points
  const calculateTeaseVoDuration = useCallback(async () => {
    console.log('[Tease VO] Calculating duration from tease in/out points...');

    try {
      const { voIn, voOut, source } = await fetchTeaseTiming(0, 10);
      const duration = voOut - voIn;
      console.log('[Tease VO] Timing source:', source);

      setTeaseVoDuration(duration);
      console.log('[Tease VO] Duration calculated from timing points:', duration, 'seconds (voIn:', voIn, 'voOut:', voOut, ')');

    } catch (error) {
      console.error('[Tease VO] Error calculating duration:', error);
      setTeaseVoDuration(null);
    }
  }, [weeklySheetId, fetchTeaseTiming]);

  // Function to load tease gain settings from P14 (global) and P15 (line-specific)
  const loadTeaseGainSettings = useCallback(async () => {
    if (!weeklySheetId) return;

    try {
      // Load week global VO gain (was P14) + tease line-specific gain (was P15).
      // Global gain is a week-level field (getSettings().globalGain); the tease
      // override is the 'Tease' section's lineGain from the VO data.
      const [settingsResp, vo] = await Promise.all([
        chartDataService.getSettings(weeklySheetId!),
        chartDataService.getVO(weeklySheetId!, getVoLanguage(weeklySheetId)),
      ]);
      const p14Value = settingsResp.globalGain;
      const teaseLineGain = vo.lineGain?.['Tease'];
      const p15Value = (teaseLineGain !== undefined && teaseLineGain !== null) ? String(teaseLineGain) : '';

      // Set global gain (was P14)
      if (p14Value && p14Value.toString().trim() !== '') {
        setGlobalGain(p14Value.toString().trim());
      } else {
        // Fall back to localStorage
        const localStorageGain = localStorage.getItem('limiterSettings_gain');
        setGlobalGain(localStorageGain && !isNaN(parseFloat(localStorageGain)) ? localStorageGain : '0');
      }

      // Set tease override (was P15) - null if empty
      if (p15Value && p15Value.toString().trim() !== '') {
        setTeaseGainOverride(p15Value.toString().trim());
      } else {
        setTeaseGainOverride(null);
      }

      console.log('[Tease Gain] Loaded settings - Global gain:', p14Value || 'empty', 'Tease Override:', p15Value || 'empty');
    } catch (error) {
      console.error('[Tease Gain] Error loading gain settings:', error);
    }
  }, [weeklySheetId]);

  // Effect to calculate tease VO duration and load gain when sheet is active
  useEffect(() => {
    if (weeklySheetId) {
      calculateTeaseVoDuration();
      loadTeaseGainSettings();
    } else {
      setTeaseVoDuration(null);
      setTeaseGainOverride(null);
      setGlobalGain('0');
    }
  }, [weeklySheetId, calculateTeaseVoDuration, loadTeaseGainSettings]);

  // Effect to handle prefill params when returning from YouTube import
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefillSlot = params.get('prefillSlot');
    const prefillUrl = params.get('prefillUrl');

    if (prefillSlot && prefillUrl && weeklySheetId) {
      console.log(`[IntroOutro] Processing prefill: slot=${prefillSlot}, url=${prefillUrl}`);

      // Persist the new URL to the slot's settings key
      const keys = getKeysForSlot(prefillSlot, activeVoLang);
      if (keys?.urlKey) {
        chartDataService.setSetting(weeklySheetId, keys.urlKey, prefillUrl).then(() => {
          console.log(`[IntroOutro] Pre-filled ${prefillSlot} with imported video URL (${keys.urlKey})`);
        }).catch((error) => {
          console.error(`[IntroOutro] Error saving prefill:`, error);
          enqueueSnackbar('Warning: Failed to save imported video', { variant: 'warning' });
        });
      }

      // Update tease settings
      setTeaseSettings(prev => ({
        ...prev,
        [`${prefillSlot}_url`]: prefillUrl,
        [`${prefillSlot}_source_type`]: 'library'
      }));

      enqueueSnackbar('Video imported and added to slot!', { variant: 'success' });

      // Clean up URL params
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('prefillSlot');
      newUrl.searchParams.delete('prefillUrl');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [location.search, weeklySheetId, enqueueSnackbar]);

  // Function to save tease gain override to P15 and sync with localStorage
  const saveTeaseGainOverride = useCallback(async (value: string) => {
    if (!weeklySheetId) return;

    const trimmedValue = value.trim();

    try {
      if (trimmedValue === '') {
        // Clear the override - will use global gain
        setTeaseGainOverride(null);
        await chartDataService.setVoField(weeklySheetId, 'tease', 'lineGain', '');
        console.log('[Tease Gain] Cleared tease line gain override, will use global gain');

        // Also clear from localStorage to stay in sync with VOManager
        const sheetId = weeklySheetId;
        const lineGainKey = `lineGainSettings_${sheetId}`;
        const exportKey = `audioExportSettings_${sheetId}`;

        try {
          const lineGainSettings = JSON.parse(localStorage.getItem(lineGainKey) || '{}');
          delete lineGainSettings['Tease'];
          localStorage.setItem(lineGainKey, JSON.stringify(lineGainSettings));

          const exportSettings = JSON.parse(localStorage.getItem(exportKey) || '{}');
          delete exportSettings['lineGain_Tease'];
          localStorage.setItem(exportKey, JSON.stringify(exportSettings));
        } catch (e) {
          console.error('[Tease Gain] Error updating localStorage:', e);
        }
      } else {
        const numericValue = parseFloat(trimmedValue);
        if (!isNaN(numericValue)) {
          const clampedValue = Math.max(-24, Math.min(24, numericValue));
          setTeaseGainOverride(clampedValue.toString());
          await chartDataService.setVoField(weeklySheetId, 'tease', 'lineGain', clampedValue.toString());
          console.log('[Tease Gain] Saved tease line gain override:', clampedValue, 'dB');

          // Also save to localStorage to stay in sync with VOManager
          const sheetId = weeklySheetId;
          const lineGainKey = `lineGainSettings_${sheetId}`;
          const exportKey = `audioExportSettings_${sheetId}`;

          try {
            const lineGainSettings = JSON.parse(localStorage.getItem(lineGainKey) || '{}');
            lineGainSettings['Tease'] = clampedValue;
            localStorage.setItem(lineGainKey, JSON.stringify(lineGainSettings));

            const exportSettings = JSON.parse(localStorage.getItem(exportKey) || '{}');
            exportSettings['lineGain_Tease'] = String(clampedValue);
            localStorage.setItem(exportKey, JSON.stringify(exportSettings));
          } catch (e) {
            console.error('[Tease Gain] Error updating localStorage:', e);
          }
        }
      }
    } catch (error) {
      console.error('[Tease Gain] Error saving gain override:', error);
      enqueueSnackbar('Failed to save gain setting', { variant: 'error' });
    }

    setIsEditingTeaseGain(false);
  }, [weeklySheetId, enqueueSnackbar]);

  const [selectedSlotForSlider, setSelectedSlotForSlider] = useState<string>('video_left');
  // Initialize tab from query params if provided (for navigation from validation modal in new tab)
  // initialSection can be 'intro', 'outro', or 'tease' - we'll map to tab index based on hasTeaseText
  const searchParams = new URLSearchParams(location.search);
  const initialSection = searchParams.get('section') as 'intro' | 'outro' | 'tease' | null;
  const [activeTab, setActiveTab] = useState<number>(0);
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [timeInputValue, setTimeInputValue] = useState<string>('0.0');
  const [isUpdatingSheet, setIsUpdatingSheet] = useState<boolean>(false);
  const [isDraggingSlider, setIsDraggingSlider] = useState<boolean>(false);
  const [currentDateString, setCurrentDateString] = useState<string>('');
  const [weeklySheetSeekInColIndex, setWeeklySheetSeekInColIndex] = useState<number>(-1);
  const [videoDuration, setVideoDuration] = useState<number>(300); // Track video duration for slider max

  // Create refs to break the circular dependency
  const loadDataRef = useRef<(() => Promise<void>) | null>(null);
  const playerAreaRef = useRef<HTMLDivElement>(null); // Ref for the player area container
  const executeSeekTimeSaveRef = useRef<((seekValueToSave: number, currentUrl: string, videoTitle: string) => Promise<void>) | null>(null);
  
  // Function to get video duration from a video element
  const getVideoDurationForSlot = useCallback((slotId: string): number => {
    try {
      // Find the video element for this slot
      const videoElement = document.querySelector(`video[data-slot-id="${slotId}"]`) as HTMLVideoElement;
      if (videoElement && videoElement.duration && !isNaN(videoElement.duration) && isFinite(videoElement.duration)) {
        console.log(`[IntroOutro] Got video duration for ${slotId}: ${videoElement.duration}s`);
        return Math.floor(videoElement.duration);
      }
    } catch (error) {
      console.warn(`[IntroOutro] Error getting video duration for ${slotId}:`, error);
    }
    
    // Fallback to default duration
    return 300;
  }, []);
  
  // Add this ref to track cleanup runs
  const cleanupRunRef = useRef<{[key: string]: boolean}>({});

  // Add a way to extract the current date from the sheet
  const [currentDate, setCurrentDate] = useState<string>('');

  // Use a ref to track dates that have had videos cleaned up
  const dateCleanupRunRef = useRef<{[key: string]: boolean}>({});

  // --- Add state for current video format ---
  const [currentFormat, setCurrentFormat] = useState<VideoFormat>('horizontal');

  // --- State for vertical video horizontal positioning ---
  const [verticalVideoXPositions, setVerticalVideoXPositions] = useState<{[key: string]: number}>({});

  // --- State for vertical video vertical positioning ---
  const [verticalVideoYPositions, setVerticalVideoYPositions] = useState<{[key: string]: number}>({});

  // --- Compute tease border URL based on chart and format ---
  const chartId = currentChartConfig?.chartId || 'hot-100';
  const teaseBorderUrl = currentFormat === 'vertical'
    ? `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/GFX/${chartId}/vertical/tease/tease_border_vertical.png`
    : `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/GFX/${chartId}/horizontal/tease/tease_border_horizontal.png`;

  // --- State for dynamic layout configuration ---
  const [dynamicLayoutConfig, setDynamicLayoutConfig] = useState<LayoutConfig>(
    currentFormat === 'horizontal' ? HORIZONTAL_DEFAULT_LAYOUT : VERTICAL_DEFAULT_LAYOUT
  );

  // --- Handler for format change ---
  const handleFormatChange = (
    event: React.MouseEvent<HTMLElement>,
    newFormat: VideoFormat | null, // newFormat can be null if no button is pressed
  ) => {
    if (newFormat) { // Check if newFormat is not null
      setCurrentFormat(newFormat);
      // TODO: Potentially trigger data reload or settings update here
      // For now, it will just change the displayed layout and background
      console.log(`[IntroOutro] Format changed to: ${newFormat}`);
    }
  };

  useEffect(() => {
    // Initialize or reset dynamicLayoutConfig when currentFormat or activeTab changes
    const newBaseLayout = currentFormat === 'horizontal' ? HORIZONTAL_DEFAULT_LAYOUT : VERTICAL_DEFAULT_LAYOUT;
    setDynamicLayoutConfig(newBaseLayout);
    console.log('[IntroOutro] dynamicLayoutConfig reset due to format/tab change.');
  }, [currentFormat, activeTab]);

  // Set initial tab from navigation state (from validation modal)
  // This runs once when hasTeaseText is determined and we have an initialSection
  const initialSectionHandledRef = useRef(false);
  useEffect(() => {
    if (initialSection && !loading && !initialSectionHandledRef.current) {
      initialSectionHandledRef.current = true;
      let tabIndex = 0;
      if (hasTeaseText) {
        // When tease exists: Tease=0, Intro=1, Outro=2
        if (initialSection === 'tease') tabIndex = 0;
        else if (initialSection === 'intro') tabIndex = 1;
        else if (initialSection === 'outro') tabIndex = 2;
      } else {
        // When no tease: Intro=0, Outro=1
        if (initialSection === 'intro') tabIndex = 0;
        else if (initialSection === 'outro') tabIndex = 1;
        // 'tease' section navigates to intro when no tease exists
        else if (initialSection === 'tease') tabIndex = 0;
      }
      console.log(`[IntroOutro] Setting initial tab to ${tabIndex} for section "${initialSection}" (hasTeaseText: ${hasTeaseText})`);
      setActiveTab(tabIndex);
    }
  }, [initialSection, hasTeaseText, loading]);

  // Effect to convert percentage positions to pixels for vertical outro draggable items
  useEffect(() => {
    const isCurrentlyOutro = hasTeaseText ? activeTab === 2 : activeTab === 1;
    if (currentFormat === 'vertical' && isCurrentlyOutro && playerAreaRef.current) {
      const parentWidth = playerAreaRef.current.offsetWidth;
      const parentHeight = playerAreaRef.current.offsetHeight;
      
      setDynamicLayoutConfig(prevConfig => {
        const newConfig = { ...prevConfig };
        let updated = false;

        Object.keys(prevConfig).forEach(slotId => {
          if (slotId.startsWith('outro_video')) { // Only for outro videos in vertical mode
            const slotLayout = { ...prevConfig[slotId] };
            const baseLayout = VERTICAL_DEFAULT_LAYOUT[slotId] || {}; // Fallback to empty if not in default

            // Convert top from percentage to pixels if needed
            if (typeof slotLayout.top === 'string' && slotLayout.top.includes('%')) {
              slotLayout.top = `${(parsePixels(baseLayout.top || '0%') / 100) * parentHeight}px`;
              updated = true;
            }
            // Convert left from percentage to pixels if needed
            if (typeof slotLayout.left === 'string' && slotLayout.left.includes('%')) {
              slotLayout.left = `${(parsePixels(baseLayout.left || '0%') / 100) * parentWidth}px`;
              updated = true;
            }
            // Ensure width and height are pixel strings
            if (typeof slotLayout.width === 'string' && !slotLayout.width.includes('px')) {
                 slotLayout.width = `${parsePixels(slotLayout.width)}px`;
                 updated = true;
            } else if (typeof slotLayout.width !== 'string') {
                slotLayout.width = `${parsePixels(baseLayout.width || '90px')}px`; // Default if not set
                updated = true;
            }

            if (typeof slotLayout.height === 'string' && !slotLayout.height.includes('px')) {
                slotLayout.height = `${parsePixels(slotLayout.height)}px`;
                updated = true;
            } else if (typeof slotLayout.height !== 'string') {
                slotLayout.height = `${parsePixels(baseLayout.height || '160px')}px`; // Default if not set
                updated = true;
            }
            newConfig[slotId] = slotLayout;
          }
        });
        if (updated) {
            console.log('[IntroOutro] Converted % to px for dynamicLayoutConfig (Vertical Outro)', newConfig);
            return newConfig;
        }
        return prevConfig;
      });
    }
  }, [currentFormat, activeTab, hasTeaseText, playerAreaRef, loading]); // Added hasTeaseText and loading to re-run when playerAreaRef becomes available

  // Add a function to save current week videos to local storage
  const saveCurrentWeekVideosToLocalStorage = useCallback((videos: MasterVideoInfo[]) => {
    if (videos.length > 0) {
      console.log(`[IntroOutro] Saving ${videos.length} current week videos to local storage`);
      localStorage.setItem(`billboard_current_week_videos_${weeklySheetId || ''}`, JSON.stringify(videos));
    }
  }, [weeklySheetId]);

  // Add a function to load current week videos from local storage
  const loadCurrentWeekVideosFromLocalStorage = useCallback((): MasterVideoInfo[] => {
    try {
      const storedVideos = localStorage.getItem(`billboard_current_week_videos_${weeklySheetId || ''}`);
      if (storedVideos) {
        const videos = JSON.parse(storedVideos) as MasterVideoInfo[];
        console.log(`[IntroOutro] Loaded ${videos.length} current week videos from local storage`);
        return videos;
      }
    } catch (error) {
      console.error('[IntroOutro] Error loading videos from local storage:', error);
    }
    return [];
  }, [weeklySheetId]);

  // Rename and modify the sync function to handle all columns
  // No longer needed since we're using the weekly sheet directly
  const syncWeeklyDataToRenderSheet = useCallback(async (unusedParam: string) => { // Function kept for compatibility but no longer needed
    console.log('[IntroOutro] No sync needed - using weekly sheet directly');
  }, []);

  // --- Data Loading functions ---
  // Helper function to normalize URLs for better matching
  const normalizeUrlForComparison = (url: string): string => {
    if (!url) return '';
    
    // Extract filename and remove query parameters
    const filename = url.split('/').pop()?.split('?')[0] || '';
    
    // Decode URL encoding multiple times to handle double-encoding
    let decoded = filename;
    for (let i = 0; i < 3; i++) {
      try {
        const newDecoded = decodeURIComponent(decoded);
        if (newDecoded === decoded) break; // No more decoding needed
        decoded = newDecoded;
      } catch {
        break; // Stop if decoding fails
      }
    }
    
    // Normalize to lowercase and remove common variations
    return decoded
      .toLowerCase()
      .replace(/[%20\s]+/g, ' ') // Replace %20 and spaces with single space
      .replace(/[%2f\/]+/g, '') // Remove slashes and %2F
      .replace(/[%2c,]+/g, '') // Remove commas and %2C  
      .replace(/[%26&]+/g, '') // Remove ampersands and %26
      .replace(/\s+/g, '') // Remove all spaces for final comparison
      .replace(/[^\w]/g, ''); // Keep only alphanumeric characters
  };

  // Helper functions for sequential slot filling
  const getSlotNumber = (slotId: string): number => {
    const match = slotId.match(/tease_shot(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const isSlotFilled = (slotId: string): boolean => {
    return !!(teaseSettings[`${slotId}_url`] && teaseSettings[`${slotId}_url`].trim() !== '');
  };

  const canFillSlot = (slotId: string): boolean => {
    const slotNumber = getSlotNumber(slotId);
    if (slotNumber <= 1) return true; // First slot can always be filled

    // Check if previous slot is filled
    const previousSlotId = `tease_shot${slotNumber - 1}`;
    return isSlotFilled(previousSlotId);
  };

  const getNextAvailableSlot = (): string | null => {
    for (let i = 1; i <= 4; i++) {
      const slotId = `tease_shot${i}`;
      if (!isSlotFilled(slotId) && canFillSlot(slotId)) {
        return slotId;
      }
    }
    return null;
  };

  // Function to handle YouTube import - navigates to UploadVideo page
  const handleYoutubeImport = useCallback(async (shotId: string, url: string) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)[\w-]+/;
    if (!youtubeRegex.test(url.trim())) {
      enqueueSnackbar('Invalid YouTube URL', { variant: 'error' });
      return;
    }

    try {
      enqueueSnackbar('Fetching video info...', { variant: 'info' });
      const songInfo = await youtubeService.getSongInfo(url);

      // Build return URL with current page info (preserve weeklySheetId)
      const currentUrl = new URL(window.location.href);
      const returnPath = `${currentUrl.pathname}${currentUrl.search}`;

      // Build URL params for UploadVideo
      const params = new URLSearchParams({
        youtubeUrl: url.trim(),
        song: songInfo.song,
        artist: songInfo.artist,
        returnUrl: returnPath,
        returnSlot: shotId
      });

      // Clear the URL input
      setYoutubeImportUrl(prev => ({ ...prev, [shotId]: '' }));

      // Navigate to UploadVideo
      navigate(`/upload-video?${params.toString()}`);

    } catch (error) {
      console.error('YouTube import error:', error);
      enqueueSnackbar(
        `Failed to get video info: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { variant: 'error' }
      );
    }
  }, [enqueueSnackbar, navigate]);

  // Function to remove video from slot
  const removeVideoFromSlot = useCallback(async (slotId: string) => {
    if (!weeklySheetId) return;

    const keys = getKeysForSlot(slotId, activeVoLang);
    if (!keys) return;

    try {
      const writes: Promise<void>[] = [];

      // Clear URL
      if (keys.urlKey) {
        writes.push(chartDataService.setSetting(weeklySheetId, keys.urlKey, ''));
      }

      // Clear seek time
      if (keys.seekKey) {
        writes.push(chartDataService.setSetting(weeklySheetId, keys.seekKey, '0'));
      }

      // Clear duration
      if (keys.durationKey) {
        writes.push(chartDataService.setSetting(weeklySheetId, keys.durationKey, '5'));
      }

      // Clear h_offset
      if (keys.hOffsetKey) {
        writes.push(chartDataService.setSetting(weeklySheetId, keys.hOffsetKey, '0'));
      }

      await Promise.all(writes);

      // Update local state
      setTeaseSettings(prev => ({
        ...prev,
        [`${slotId}_url`]: '',
        [`${slotId}_seek`]: '0',
        [`${slotId}_duration`]: '5'
      }));

      // Reset horizontal position
      setVerticalVideoXPositions(prev => ({
        ...prev,
        [slotId]: 0
      }));

      console.log(`[IntroOutro] Removed video from ${slotId}`);
    } catch (error) {
      console.error(`[IntroOutro] Failed to remove video from ${slotId}:`, error);
    }
  }, [weeklySheetId]);

  // Function to save horizontal position to Google Sheets
  const saveHorizontalPositionToSheet = useCallback(async (slotId: string, position: number) => {
    if (!weeklySheetId) return;

    const keys = getKeysForSlot(slotId, activeVoLang);
    if (!keys?.hOffsetKey) {
      console.warn(`[IntroOutro] No h_offset key defined for slot: ${slotId}`);
      return;
    }

    try {
      await chartDataService.setSetting(weeklySheetId, keys.hOffsetKey, position.toString());

      console.log(`[IntroOutro] Saved horizontal position ${position} to ${keys.hOffsetKey} for ${slotId}`);
    } catch (error) {
      console.error(`[IntroOutro] Failed to save horizontal position for ${slotId}:`, error);
    }
  }, [weeklySheetId]);

  // Function to save vertical position to Google Sheets
  const saveVerticalPositionToSheet = useCallback(async (slotId: string, position: number) => {
    if (!weeklySheetId) return;

    const keys = getKeysForSlot(slotId, activeVoLang);
    if (!keys?.vOffsetKey) {
      console.warn(`[IntroOutro] No v_offset key defined for slot: ${slotId}`);
      return;
    }

    try {
      await chartDataService.setSetting(weeklySheetId, keys.vOffsetKey, position.toString());

      console.log(`[IntroOutro] Saved vertical position ${position} to ${keys.vOffsetKey} for ${slotId}`);
    } catch (error) {
      console.error(`[IntroOutro] Failed to save vertical position for ${slotId}:`, error);
    }
  }, [weeklySheetId]);

  const cleanupOutdatedVideos = useCallback(async (weekVideos: MasterVideoInfo[]) => {
    if (!weeklySheetId) return; // Only return if no weeklySheetId, but continue if weekVideos is empty
    
    console.log('[IntroOutro] Checking for outdated videos to clean up...');
    console.log(`[IntroOutro] Current sheet ID: ${weeklySheetId}`);
    console.log(`[IntroOutro] Current date: ${currentDateString}`);
    console.log(`[IntroOutro] Current week videos count: ${weekVideos.length}`);
    
    // Get normalized list of current week video filenames for comparison
    const currentVideoFilenames = weekVideos.map(v => {
      const normalizedFilename = normalizeUrlForComparison(v.s3Url);
      console.log(`[IntroOutro] Current week video: ${normalizedFilename} (${v.song} by ${v.artist})`);
      return normalizedFilename;
    });
    
    console.log(`[IntroOutro] Current week has ${currentVideoFilenames.length} videos`);
    
    // Define intro and outro slots
    const introSlots = ['carousel_video1', 'carousel_video2', 'carousel_video3', 'carousel_video4', 'video_left'];
    const outroSlots = ['outro_video1', 'outro_video2', 'outro_video3', 'outro_video4', 'outro_video5', 'outro_video6'];
    const allSlots = [...introSlots, ...outroSlots];
    
    // Array to collect settings writes (key/value) to flush together
    const updates: { key: string; value: string }[] = [];
    const removedVideos: {slotId: string; song?: string; artist?: string; url: string}[] = [];
    
    // Loop through all possible intro/outro slots and check if they contain current week videos
    for (const slotId of allSlots) {
      // Get the URL for this slot
      let slotUrl = '';
      if (introSlots.includes(slotId)) {
        slotUrl = (introSettings as any)[`${slotId}_url`] || '';
      } else if (outroSlots.includes(slotId)) {
        slotUrl = (outroSettings as any)[`${slotId}_url`] || '';
      }
      
      if (!slotUrl) {
        console.log(`[IntroOutro] Skipping empty slot: ${slotId}`);
        continue; // Skip empty slots
      }
      
      // Normalize the slot URL for comparison
      const normalizedSlotFilename = normalizeUrlForComparison(slotUrl);
      console.log(`[IntroOutro] Checking ${slotId} with normalized filename: ${normalizedSlotFilename}`);
      
      // If we have no videos in current week or the video isn't in the current week's list, clean it up
      let isInCurrentWeek = false;
      
      // Only check if video is in current week if we have videos to check against
      if (currentVideoFilenames.length > 0) {
        isInCurrentWeek = currentVideoFilenames.some(currentFilename => {
          // Use normalized comparison
          const match = currentFilename === normalizedSlotFilename;
          if (match) {
            console.log(`[IntroOutro] Normalized filename match: ${normalizedSlotFilename} === ${currentFilename}`);
          }
          return match;
        });
      }
      
      console.log(`[IntroOutro] Is ${slotId} in current week's top 10? ${isInCurrentWeek}`);
      
      // If the video is not in the current week (or there are no current week videos), clean it up
      if (!isInCurrentWeek) {
        console.log(`[IntroOutro] Cleaning up ${slotId} as it's not in current week's top 10`);
        
        // Get settings keys for this slot
        const keys = getKeysForSlot(slotId, activeVoLang);
        if (keys) {
          // Add to pending settings writes
          updates.push({ key: keys.urlKey, value: '' });
          if (keys.seekKey) updates.push({ key: keys.seekKey, value: '0' });

          // Try to find song and artist info for this URL
          let songInfo = null;
          
          // Helper function to extract and normalize filename
          const extractFilename = (fileUrl: string): string => {
            const filename = fileUrl.split('/').pop() || '';
            return filename.split('?')[0].toLowerCase();
          };
          
          // Look in master video map
          songInfo = Array.from(masterVideoMap.values()).find(info => 
            info.s3Url.trim() === slotUrl.trim() || 
            extractFilename(info.s3Url) === extractFilename(slotUrl)
          );
          
          // If not found, check current week videos (unlikely but just in case)
          if (!songInfo) {
            songInfo = currentWeekVideos.find(info => 
              info.s3Url.trim() === slotUrl.trim() || 
              extractFilename(info.s3Url) === extractFilename(slotUrl)
            );
          }
          
          // Add to removed videos
          removedVideos.push({
            slotId,
            song: songInfo?.song,
            artist: songInfo?.artist, 
            url: slotUrl
          });
        }
      }
    }
    
    // If we have updates, persist the settings writes
    if (updates.length > 0 && weeklySheetId) {
      console.log(`[IntroOutro] Cleaning up ${removedVideos.length} outdated videos`);

      try {
        await Promise.all(updates.map(u => chartDataService.setSetting(weeklySheetId, u.key, u.value)));
        console.log('[IntroOutro] Successfully cleaned up outdated videos');
        
        // Update local state
        for (const video of removedVideos) {
          if (introSlots.includes(video.slotId)) {
            setIntroSettings(prev => ({
              ...prev,
              [`${video.slotId}_url`]: '',
              [`${video.slotId}_seek`]: '0'
            }));
          } else if (outroSlots.includes(video.slotId)) {
            setOutroSettings(prev => ({
              ...prev,
              [`${video.slotId}_url`]: '',
              [`${video.slotId}_seek`]: '0'
            }));
          }
        }
        
        // Show cleanup notification
        if (removedVideos.length > 0) {
          // Deduplicate videos by URL to avoid showing the same song multiple times
          const uniqueRemovedVideos = removedVideos.reduce((unique, video) => {
            const existingVideo = unique.find(u => u.url === video.url);
            if (!existingVideo) {
              unique.push(video);
            }
            return unique;
          }, [] as typeof removedVideos);
          
          const removedMessages = uniqueRemovedVideos.map(video => {
            // Try to get song and artist from songInfo
            if (video.song && video.artist) {
              return `Removed "${video.song}" by ${video.artist} because it's not in this week's chart.`;
            } else {
              // If song and artist info not available, try to extract from filename
              const filename = video.url.split('/').pop() || '';
              const decodedFilename = decodeURIComponent(filename);
              
              // Attempt to parse song and artist from filename
              let song = "";
              let artist = "";
              
              // Common filename patterns: "Artist - Song.mp4" or "Song - Artist.mp4"
              if (decodedFilename.includes(' - ')) {
                const parts = decodedFilename.split(' - ');
                if (parts.length >= 2) {
                  // Assume first part is artist, second part is song (removing .mp4 extension)
                  artist = parts[0].trim();
                  song = parts[1].replace(/\.(mp4|mov|avi|webm)$/i, '').trim();
                  
                  // Check if we need to swap (song tends to have more words than artist name)
                  // This is a heuristic that might not always be correct
                  if (artist.split(' ').length > song.split(' ').length && !artist.includes('feat.')) {
                    // Swap them - artist name is usually shorter than song title
                    [artist, song] = [song, artist];
                  }
                }
              } else {
                // If no separator, just use the filename as the song
                song = decodedFilename.replace(/\.(mp4|mov|avi|webm)$/i, '');
                artist = "Unknown Artist";
              }
              
              return `Removed "${song}" by ${artist} because it's not in this week's chart.`;
            }
          });
          
          setCleanupNotification(removedMessages.join('\n'));
          // Clear notification after 15 seconds
          setTimeout(() => setCleanupNotification(null), 15000);
        }
      } catch {
        console.error('[IntroOutro] Error cleaning up videos');
        setError('Failed to clean up outdated videos.');
      }
    } else {
      console.log('[IntroOutro] No videos to clean up');
    }
  }, [weeklySheetId, introSettings, outroSettings, masterVideoMap, currentWeekVideos, currentDateString]);

  // Function to format date string to match OutputManager format (M-D-YY)
  const formatSheetDate = useCallback((dateString: string): string => {
    if (!dateString) return 'unknown-date';

    try {
      const dateObj = new Date(dateString);
      if (!isNaN(dateObj.getTime())) {
        const month = dateObj.getMonth() + 1;
        const day = dateObj.getDate();
        const year = dateObj.getFullYear().toString().slice(-2); // YY format
        const formatted = `${month}-${day}-${year}`;
        console.log(`[IntroOutro] Formatted date from '${dateString}' to '${formatted}'`);
        return formatted;
      }
    } catch (e) {
      console.error('Error formatting sheet date:', e);
    }
    return 'unknown-date';
  }, []);

  // (Removed) checkTeaseVoExists: it looked up a tease.wav by convention S3 path
  // and adopted it if present. That trusted a file's PRESENCE, not its
  // provenance, so a leftover or wrong-language tease at the path leaked into
  // the render. The tease VO is now authoritative only from this week's
  // per-language settings (set at generation time). No path-existence fallback.

  // Function to stop preview playback
  const stopPreview = useCallback(() => {
    console.log('[Preview] Stopping preview...');

    // Stop audio playback
    if (previewAudioRef) {
      previewAudioRef.pause();
      previewAudioRef.currentTime = 0;
    }

    // Clear all timeouts
    previewTimeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    previewTimeoutRefs.current = [];

    // Clear progress interval
    if (previewIntervalRef.current) {
      clearInterval(previewIntervalRef.current);
      previewIntervalRef.current = null;
    }

    // Reset state
    setIsPlayingPreview(false);
    setPreviewProgress(0);

    // Stop all video playback and reset positions
    ['tease_shot1', 'tease_shot2', 'tease_shot3', 'tease_shot4'].forEach(shotId => {
      const videoElement = document.querySelector(`video[data-slot-id="${shotId}"]`) as HTMLVideoElement;
      if (videoElement) {
        videoElement.pause();
        const seekTime = parseFloat(teaseSettings[`${shotId}_seek`] || '0');
        videoElement.currentTime = seekTime;
      }
    });
  }, [previewAudioRef, teaseSettings]);

  // Function to manually stop preview (used by Stop Preview button)
  const manualStopPreview = useCallback(() => {
    setShowRedProgressBars(false);
    stopPreview();
  }, [stopPreview]);

  // Function to start preview playback
  const startPreview = useCallback(async () => {
    console.log('[Preview] Starting tease preview...');

    // Stop any existing preview
    stopPreview();

    // Fetch line-specific tease VO (was S15), or fall back to tease VO from settings
    let teaseVoUrl = '';
    try {
      console.log('[Preview] Checking for line-specific tease VO...');
      const vo = await chartDataService.getVO(weeklySheetId!, getVoLanguage(weeklySheetId));
      const lineVoUrl = vo.lineVoUrls?.['Tease'] || '';

      if (lineVoUrl && lineVoUrl.trim()) {
        teaseVoUrl = lineVoUrl.trim();
        console.log('[Preview] Using line-specific tease VO:', teaseVoUrl);
      } else {
        // Fall back to tease VO from settings (tease_intro_voiceover_url or previously loaded)
        teaseVoUrl = teaseSettings.tease_intro_voiceover_url || processedAudioUrl || '';
        console.log('[Preview] Using tease VO from settings:', teaseVoUrl);
      }
    } catch (error) {
      console.error('[Preview] Error fetching VO URL, using settings:', error);
      teaseVoUrl = teaseSettings.tease_intro_voiceover_url || processedAudioUrl || '';
    }

    if (!teaseVoUrl) {
      enqueueSnackbar('No tease voiceover available for preview', { variant: 'error' });
      return;
    }

    // Fetch tease timing (prefers T15/U15 override, falls back to N15/O15)
    let voIn = 0;
    let voOut = 10; // Default fallback
    try {
      console.log('[Preview] Fetching tease timing...');
      const timing = await fetchTeaseTiming(0, 10);
      voIn = timing.voIn;
      voOut = timing.voOut;
      console.log('[Preview] Using tease timing from', timing.source, '- voIn:', voIn, 'voOut:', voOut);
    } catch (error) {
      console.error('[Preview] Error fetching tease timing, using defaults:', error);
    }

    // Get configured shots
    const shots = ['tease_shot1', 'tease_shot2', 'tease_shot3', 'tease_shot4']
      .map(shotId => ({
        id: shotId,
        url: teaseSettings[`${shotId}_url`],
        seek: parseFloat(teaseSettings[`${shotId}_seek`] || '0'),
        duration: parseFloat(teaseSettings[`${shotId}_duration`] || '2.5')
      }))
      .filter(shot => shot.url); // Only include configured shots

    if (shots.length === 0) {
      enqueueSnackbar('No shots configured for preview', { variant: 'error' });
      return;
    }

    console.log('[Preview] Configured shots:', shots);

    // Create and load audio element with immediate user interaction
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.src = teaseVoUrl;
    setPreviewAudioRef(audio);

    try {
      // Set playing state immediately to show progress UI
      setIsPlayingPreview(true);
      setShowRedProgressBars(true);

      // Wait for metadata to load so we can set currentTime
      await new Promise<void>((resolve) => {
        audio.addEventListener('loadedmetadata', () => resolve(), { once: true });
      });

      // Set audio start position to voIn
      audio.currentTime = voIn;
      console.log('[Preview] Set audio start position to:', voIn);

      // Try to play immediately with user interaction
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        await playPromise;
        console.log('[Preview] Audio playback started successfully');
      }

      // Calculate actual preview duration from voIn to voOut
      const totalDuration = voOut - voIn;
      console.log('[Preview] Preview duration (voOut - voIn):', totalDuration, 'seconds');
      setPreviewDuration(totalDuration);

      // Schedule audio stop at voOut time
      const audioStopTimeout = setTimeout(() => {
        console.log('[Preview] Stopping audio at voOut:', voOut);
        audio.pause();
        stopPreview();
      }, totalDuration * 1000);
      previewTimeoutRefs.current.push(audioStopTimeout);

      // Start progress tracking (relative to voIn)
      previewIntervalRef.current = setInterval(() => {
        if (audio && !audio.paused) {
          const progress = audio.currentTime - voIn; // Show progress relative to voIn
          setPreviewProgress(progress);

          // Extra safety check to stop if we reach voOut
          if (audio.currentTime >= voOut) {
            console.log('[Preview] Reached voOut, stopping audio');
            audio.pause();
            stopPreview();
          }
        }
      }, 100);

      // Calculate backtiming for video shots
      const totalShotDuration = shots.reduce((sum, shot) => sum + shot.duration, 0);
      const videoStartDelay = totalDuration - totalShotDuration;

      console.log('[Preview] Video timing:', {
        totalDuration,
        totalShotDuration,
        videoStartDelay: Math.max(0, videoStartDelay)
      });

      // First, pause all videos to stop MiniVideoPlayer autoplay
      shots.forEach(shot => {
        const videoElement = document.querySelector(`video[data-slot-id="${shot.id}"]`) as HTMLVideoElement;
        if (videoElement && !videoElement.paused) {
          videoElement.pause();
        }
      });

      // Schedule video playback
      let currentVideoTime = Math.max(0, videoStartDelay);

      shots.forEach((shot, index) => {
        const shotStartTime = currentVideoTime; // Capture the start time for this specific shot
        const timeout = setTimeout(() => {

          // Add a small retry mechanism for finding video elements
          const findAndPlayVideo = (retries = 3) => {
            const videoElement = document.querySelector(`video[data-slot-id="${shot.id}"]`) as HTMLVideoElement;
            if (videoElement) {
              videoElement.currentTime = shot.seek;
              videoElement.play().catch(e => {
                console.warn(`[Preview] Failed to play video ${shot.id}:`, e);
              });

              // Stop this video after its duration
              const stopTimeout = setTimeout(() => {
                if (videoElement) {
                  videoElement.pause();
                }
              }, shot.duration * 1000);

              previewTimeoutRefs.current.push(stopTimeout);
            } else if (retries > 0) {
              setTimeout(() => findAndPlayVideo(retries - 1), 100);
            }
          };

          findAndPlayVideo();
        }, shotStartTime * 1000);

        previewTimeoutRefs.current.push(timeout);
        currentVideoTime += shot.duration;
      });


      // Auto-stop preview when audio ends
      const endTimeout = setTimeout(() => {
        setShowRedProgressBars(false);
        stopPreview();
      }, totalDuration * 1000);

      previewTimeoutRefs.current.push(endTimeout);

    } catch (error) {
      console.error('[Preview] Failed to start preview:', error);
      setIsPlayingPreview(false);

      // Provide more helpful error messages
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          enqueueSnackbar('Audio autoplay blocked by browser. Try clicking again or enable autoplay for this site.', { variant: 'warning' });
        } else if (error.name === 'NotSupportedError') {
          enqueueSnackbar('Audio format not supported by browser', { variant: 'error' });
        } else {
          enqueueSnackbar(`Preview failed: ${error.message}`, { variant: 'error' });
        }
      } else {
        enqueueSnackbar('Failed to start preview playback. Try clicking again.', { variant: 'warning' });
      }
    }
  }, [teaseSettings, stopPreview, enqueueSnackbar]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPreview();
    };
  }, [stopPreview]);

  // Computed variable for tease VO availability - check B63, S15, or M14
  const hasTeaseVoUrl = !!teaseSettings.tease_intro_voiceover_url || !!processedAudioUrl;

  // Add a function to extract date from sheet if possible
  const extractDateFromSheet = useCallback(async () => {
    if (!weeklySheetId) return;
    
    try {
      const settingsResp = await chartDataService.getSettings(weeklySheetId);
      const dateString = String(settingsResp.dateString || '');
      if (dateString) {
        console.log(`[IntroOutro] Extracted date from chart week: ${dateString}`);
        setCurrentDateString(dateString);
        
        // Reset cleanup tracking whenever a new date is loaded
        if (dateString && !dateCleanupRunRef.current[dateString]) {
          console.log(`[IntroOutro] New date detected: ${dateString}, resetting cleanup tracking`);
          dateCleanupRunRef.current[dateString] = true;
          cleanupRunRef.current = {}; // Reset video cleanup status for new date
        }
      }
    } catch (err) {
      console.error('[IntroOutro] Error extracting date from sheet:', err);
    }
  }, [weeklySheetId]);

  // Function to sync outdated seek points when weekly sheet is opened
  const syncOutdatedSeekPoints = useCallback(async (): Promise<void> => {
    if (!weeklySheetId || masterVideoMap.size === 0) return;

    try {
      console.log('[IntroOutro] Checking for outdated seek points in carousel positions...');
      
      const allSlots = ['carousel_video1', 'carousel_video2', 'carousel_video3', 'carousel_video4', 'video_left', 'outro_video1', 'outro_video2', 'outro_video3', 'outro_video4', 'outro_video5', 'outro_video6'];
      const updatesNeeded: { key: string; value: string }[] = [];
      let outdatedCount = 0;

      // Check each carousel position
      for (const slotId of allSlots) {
        const isSlotIntro = slotId.startsWith('carousel') || slotId === 'video_left';
        const slotSettings = isSlotIntro ? introSettings : outroSettings;
        const slotUrl = (slotSettings as any)[`${slotId}_url`] || '';
        const currentSeekStr = (slotSettings as any)[`${slotId}_seek`] || '0';

        if (slotUrl) {
          // Find the master seek time for this video
          const masterVideo = masterVideoMap.get(slotUrl);
          if (masterVideo) {
            const masterSeekTime = parseFloat(masterVideo.seekIn);
            const currentSeekTime = parseFloat(currentSeekStr);

            // Check if they're different (with small tolerance for floating point comparison)
            if (Math.abs(masterSeekTime - currentSeekTime) > 0.01) {
              console.log(`[IntroOutro] Outdated seek point found in ${slotId}: current=${currentSeekTime}s, master=${masterSeekTime}s`);

              const slotKeys = getKeysForSlot(slotId, activeVoLang);
              if (slotKeys?.seekKey) {
                updatesNeeded.push({ key: slotKeys.seekKey, value: masterSeekTime.toString() });

                // Update local state
                if (isSlotIntro) {
                  setIntroSettings(prev => ({ ...prev, [`${slotId}_seek`]: masterSeekTime.toString() }));
                } else {
                  setOutroSettings(prev => ({ ...prev, [`${slotId}_seek`]: masterSeekTime.toString() }));
                }
                
                outdatedCount++;
              }
            }
          }
        }
      }

      // The per-position seek for current-week videos lives on the linked video
      // row in the Master Video List (videos.seek_in), which is the single source
      // of truth (masterVideoMap mirrors it). There is no separate weekly "Seek In"
      // column in the DB model — positions derive their seek from the linked video
      // — so a stale-weekly-mirror sync is unnecessary: keeping the Master row in
      // sync (done at edit time via updateMasterVideoListSeekTime) is sufficient.

      // Apply all the carousel/outro slot-seek settings writes
      if (updatesNeeded.length > 0) {
        await Promise.all(updatesNeeded.map(u => chartDataService.setSetting(weeklySheetId, u.key, u.value)));

        // Show notification
        if (outdatedCount > 0) {
          enqueueSnackbar(`Synced ${outdatedCount} outdated seek point${outdatedCount > 1 ? 's' : ''} with Master Video List`, {
            variant: 'info'
          });
        }

        console.log(`[IntroOutro] Synced ${updatesNeeded.length} outdated seek points with Master Video List`);
      } else {
        console.log('[IntroOutro] All seek points are up to date');
      }
    } catch (error) {
      console.error('[IntroOutro] Error syncing outdated seek points:', error);
      // Don't show error to user as this is background sync
    }
  }, [weeklySheetId, masterVideoMap, introSettings, outroSettings, currentWeekVideos, weeklySheetSeekInColIndex, setIntroSettings, setOutroSettings, enqueueSnackbar]);

  // Update loadData to call extractDateFromSheet
  const loadData = useCallback(async () => {
    if (!weeklySheetId) {
      setError('Weekly Sheet ID not provided.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Extract date from sheet
      await extractDateFromSheet();
      
      // Rest of existing loadData implementation...

      // Get the sheet name from location state
      const sheetName = location.state?.sheetName || '';
      
      // Parse date from sheet name if it matches the pattern (e.g., "5-3-25-render")
      if (sheetName.includes('-render')) {
        const dateMatch = sheetName.match(/(\d+)-(\d+)-(\d+)-render/);
        if (dateMatch) {
          const [, month, day, year] = dateMatch;
          // Convert month number to month name
          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                            'July', 'August', 'September', 'October', 'November', 'December'];
          const monthIndex = parseInt(month) - 1;
          const fullYear = parseInt(year) < 100 ? 2000 + parseInt(year) : parseInt(year);
          
          // Format the date (e.g., "May 3, 2025")
          const formattedDate = `${monthNames[monthIndex]} ${parseInt(day)}, ${fullYear}`;
          
          // Persist the formatted date (week-level date_string field)
          await chartDataService.setSetting(weeklySheetId, 'dateString', formattedDate);

          // Update App component state if setChartDate prop is available
          if (setChartDate) {
            setChartDate(formattedDate);
          }
          
          console.log(`[IntroOutro] Updated sheet date to: ${formattedDate}`);
        }
      }

      // First try to load videos from local storage for immediate use
      const cachedVideos = loadCurrentWeekVideosFromLocalStorage();
      if (cachedVideos.length > 0) {
        setCurrentWeekVideos(cachedVideos);
        
        // Debug if Ordinary is in cached videos
        const ordinaryInCached = cachedVideos.find(v => v.song === "Ordinary" || v.s3Url.toLowerCase().includes("ordinary"));
        if (ordinaryInCached) {
          console.log(`[IntroOutro] Found Ordinary in cached videos: seekIn = ${ordinaryInCached.seekIn}, URL = ${ordinaryInCached.s3Url}`);
        } else {
          console.log(`[IntroOutro] Ordinary NOT found in cached videos`);
        }
      }

      // 1. Fetch Master Video List (typed videos table — no spreadsheet ranges)
      console.log('[IntroOutro] Fetching Master Video List...');
      const masterVideos = await chartDataService.listVideos();
      if (!masterVideos) {
        console.error('[IntroOutro] Failed to load master video list or no data available.');
        return;
      }

      {
        console.log(`[IntroOutro] Processing data from Master Video List, ${masterVideos.length} videos...`);

        const newMasterMap = new Map<string, MasterVideoInfo>();

        masterVideos.forEach((v) => {
          // Skip rows without an S3 URL
          if (!v.s3Url) return;

          const s3Url = String(v.s3Url ?? '').trim();
          const seekIn = String(v.seekIn ?? '0').trim();
          const song = String(v.song ?? '').trim();
          const artist = String(v.artist ?? '').trim();

          if (s3Url) {
            newMasterMap.set(s3Url, { s3Url, seekIn, song, artist });

            // Special debug for Ordinary and Beautiful Things
            if (song === "Ordinary" || s3Url.toLowerCase().includes("ordinary") ||
                song === "Beautiful Things" || s3Url.toLowerCase().includes("beautiful things")) {
              console.log(`[IntroOutro] Added ${song} to master map: seekIn = ${seekIn}, URL = ${s3Url}`);
            }

            // Debug for the specific problematic songs mentioned by user
            const problemSongs = ['luther', 'bar song', 'lose control', 'die with a smile'];
            if (problemSongs.some(problem => song.toLowerCase().includes(problem) || s3Url.toLowerCase().includes(problem.replace(/\s+/g, '')))) {
              console.log(`[IntroOutro DEBUG] Added problem song "${song}" to master map: seekIn = ${seekIn}, URL = ${s3Url}`);
            }
          }
        });

        setMasterVideoMap(newMasterMap);
        console.log(`[IntroOutro] Master Video List loaded with ${newMasterMap.size} entries.`);

        // Debug check for Ordinary in master map
        console.log('[IntroOutro] Checking for Ordinary in master map:');
        const ordinaryEntries = Array.from(newMasterMap.values()).filter(
          (v: MasterVideoInfo) => v.song === "Ordinary" || v.s3Url.toLowerCase().includes("ordinary")
        );
        if (ordinaryEntries.length > 0) {
          ordinaryEntries.forEach((entry: MasterVideoInfo) => {
            console.log(`[IntroOutro] Master map has Ordinary: seekIn = ${entry.seekIn}, URL = ${entry.s3Url}`);
          });
        } else {
          console.log('[IntroOutro] Ordinary NOT found in master map');
        }
      }

      // 2. Fetch Settings for this chart week (settings JSONB key→value) + VO data
      console.log(`[IntroOutro] Fetching settings for chart week: ${weeklySheetId}`);
      const [settingsResp, voResp] = await Promise.all([
        chartDataService.getSettings(weeklySheetId),
        chartDataService.getVO(weeklySheetId, getVoLanguage(weeklySheetId)),
      ]);
      const settingsValues = settingsResp.settings || {};
      console.log('[IntroOutro loadData] Settings keys received:', Object.keys(settingsValues));

      // Tease text (was L15) = the 'Tease' section VO script text
      const teaseText = voResp.voData?.['Tease'] || '';
      const hasTeaseContent = teaseText.trim().length > 0;
      setHasTeaseText(hasTeaseContent);
      console.log(`[IntroOutro loadData] Tease text: "${teaseText}", hasTeaseContent: ${hasTeaseContent}`);

      const newIntroSettings: IntroSettings = {};
      const newOutroSettings: OutroSettings = {};
      const newTeaseSettings: TeaseSettings = {};

      // Helper: read a settings value by its canonical key (was a B<row> cell).
      const getSettingValue = (key: string): string | undefined => {
        const value = settingsValues[key];
        const out = (value === undefined || value === null || value === '') ? undefined : String(value);
        console.log(`[IntroOutro loadData] Setting ${key} = ${out ?? 'undefined'}`);
        return out;
      };


      // Populate Intro Settings using the helper
      newIntroSettings.carousel_video1_url = getSettingValue('carousel_video1_url') || '';
      newIntroSettings.carousel_video1_seek = getSettingValue('carousel_video1_seek') || '0';
      newIntroSettings.carousel_video2_url = getSettingValue('carousel_video2_url') || '';
      newIntroSettings.carousel_video2_seek = getSettingValue('carousel_video2_seek') || '0';
      newIntroSettings.carousel_video3_url = getSettingValue('carousel_video3_url') || '';
      newIntroSettings.carousel_video3_seek = getSettingValue('carousel_video3_seek') || '0';
      newIntroSettings.carousel_video4_url = getSettingValue('carousel_video4_url') || '';
      newIntroSettings.carousel_video4_seek = getSettingValue('carousel_video4_seek') || '0';
      newIntroSettings.video_left_url = getSettingValue('video_left_url') || '';
      newIntroSettings.video_left_seek = getSettingValue('video_left_seek') || '0';

      // Get the chart date (was B28) — the week-level date_string field
      const dateFromSheet = settingsResp.dateString || '';
      if (dateFromSheet) {
        console.log(`[IntroOutro] Found chart date: ${dateFromSheet}`);

        // Update App component state if setChartDate prop is available
        if (setChartDate) {
          setChartDate(dateFromSheet);
        }
      }

      // Populate Outro Settings using the helper
      newOutroSettings.outro_video1_url = getSettingValue('outro_video1_url') || '';
      newOutroSettings.outro_video1_seek = getSettingValue('outro_video1_seek') || '0';
      newOutroSettings.outro_video2_url = getSettingValue('outro_video2_url') || '';
      newOutroSettings.outro_video2_seek = getSettingValue('outro_video2_seek') || '0';
      newOutroSettings.outro_video3_url = getSettingValue('outro_video3_url') || '';
      newOutroSettings.outro_video3_seek = getSettingValue('outro_video3_seek') || '0';
      newOutroSettings.outro_video4_url = getSettingValue('outro_video4_url') || '';
      newOutroSettings.outro_video4_seek = getSettingValue('outro_video4_seek') || '0';
      newOutroSettings.outro_video5_url = getSettingValue('outro_video5_url') || '';
      newOutroSettings.outro_video5_seek = getSettingValue('outro_video5_seek') || '0';
      newOutroSettings.outro_video6_url = getSettingValue('outro_video6_url') || '';
      newOutroSettings.outro_video6_seek = getSettingValue('outro_video6_seek') || '0';

      // Populate Tease Settings using the helper. The tease VO URL is language-
      // scoped (separate tease per language on bilingual charts); tease shot
      // settings are shared visuals so they stay unsuffixed.
      newTeaseSettings.tease_intro_voiceover_url = getSettingValue(teaseSettingKey('tease_intro_voiceover_url', activeVoLang)) || '';
      // Tease Shot 1
      newTeaseSettings.tease_shot1_url = getSettingValue('tease_shot1_url') || '';
      newTeaseSettings.tease_shot1_seek = getSettingValue('tease_shot1_seek') || '0';
      newTeaseSettings.tease_shot1_duration = getSettingValue('tease_shot1_duration') || '5';
      newTeaseSettings.tease_shot1_h_offset = getSettingValue('tease_shot1_h_offset') || '0';
      newTeaseSettings.tease_shot1_v_offset = getSettingValue('tease_shot1_v_offset') || '0';
      // Tease Shot 2
      newTeaseSettings.tease_shot2_url = getSettingValue('tease_shot2_url') || '';
      newTeaseSettings.tease_shot2_seek = getSettingValue('tease_shot2_seek') || '0';
      newTeaseSettings.tease_shot2_duration = getSettingValue('tease_shot2_duration') || '5';
      newTeaseSettings.tease_shot2_h_offset = getSettingValue('tease_shot2_h_offset') || '0';
      newTeaseSettings.tease_shot2_v_offset = getSettingValue('tease_shot2_v_offset') || '0';
      // Tease Shot 3
      newTeaseSettings.tease_shot3_url = getSettingValue('tease_shot3_url') || '';
      newTeaseSettings.tease_shot3_seek = getSettingValue('tease_shot3_seek') || '0';
      newTeaseSettings.tease_shot3_duration = getSettingValue('tease_shot3_duration') || '5';
      newTeaseSettings.tease_shot3_h_offset = getSettingValue('tease_shot3_h_offset') || '0';
      newTeaseSettings.tease_shot3_v_offset = getSettingValue('tease_shot3_v_offset') || '0';
      // Tease Shot 4
      newTeaseSettings.tease_shot4_url = getSettingValue('tease_shot4_url') || '';
      newTeaseSettings.tease_shot4_seek = getSettingValue('tease_shot4_seek') || '0';
      newTeaseSettings.tease_shot4_duration = getSettingValue('tease_shot4_duration') || '5';
      newTeaseSettings.tease_shot4_h_offset = getSettingValue('tease_shot4_h_offset') || '0';
      newTeaseSettings.tease_shot4_v_offset = getSettingValue('tease_shot4_v_offset') || '0';

      // Load generated tease movie URLs (was B84/B85), language-scoped
      const teaseHorizontalUrl = getSettingValue(teaseSettingKey('tease_horizontal', activeVoLang)) || '';
      const teaseVerticalUrl = getSettingValue(teaseSettingKey('tease_vertical', activeVoLang)) || '';

      // Log the loaded settings
      // ---> Log final objects before setState
      console.log('[IntroOutro loadData] Final introData before setState:', JSON.stringify(newIntroSettings, null, 2));
      console.log('[IntroOutro loadData] Final outroData before setState:', JSON.stringify(newOutroSettings, null, 2));
      console.log('[IntroOutro loadData] Final teaseData before setState:', JSON.stringify(newTeaseSettings, null, 2));
      // ---> END: Log final objects

      // Prevent setting empty objects if our data retrieval failed
      if (Object.keys(newIntroSettings).length > 0 || Object.values(newIntroSettings).some(v => v !== '' && v !== '0')) {
        // Simplified: set directly, as settings are shared
        setIntroSettings(newIntroSettings);
      } else {
        console.error('[IntroOutro] Failed to load intro settings - object is empty');
      }
      
      if (Object.keys(newOutroSettings).length > 0 || Object.values(newOutroSettings).some(v => v !== '' && v !== '0')) {
        // Simplified: set directly, as settings are shared
        setOutroSettings(newOutroSettings);
      } else {
        console.error('[IntroOutro] Failed to load outro settings - object is empty');
      }

      // Set tease settings (always set, even if empty, since we need the structure)
      setTeaseSettings(newTeaseSettings);
      console.log('[IntroOutro] Tease settings loaded and set');

      // If tease URLs exist in the sheet (B84 and B85), populate the generated tease URLs
      if (teaseHorizontalUrl || teaseVerticalUrl) {
        setGeneratedTeaseUrls({
          horizontal: teaseHorizontalUrl,
          vertical: teaseVerticalUrl
        });
        console.log('[IntroOutro] Loaded existing tease URLs from sheet B84/B85:', {
          horizontal: teaseHorizontalUrl,
          vertical: teaseVerticalUrl
        });
      }

      // Auto-detect uploaded videos and set source_type accordingly
      const updatedTeaseSettings = { ...newTeaseSettings };
      let hasUpdates = false;

      ['tease_shot1', 'tease_shot2', 'tease_shot3', 'tease_shot4'].forEach(shotId => {
        const url = newTeaseSettings[`${shotId}_url`];
        if (url && url.includes('/Custom/')) {
          // This is an uploaded video in the Custom folder
          updatedTeaseSettings[`${shotId}_source_type`] = 'upload';
          hasUpdates = true;
          console.log(`[IntroOutro] Auto-detected uploaded video for ${shotId}: ${url}`);
        }
      });

      if (hasUpdates) {
        setTeaseSettings(updatedTeaseSettings);
        console.log('[IntroOutro] Updated source_type for uploaded videos');
      }

      // Populate horizontal video positions from h_offset values
      const newHorizontalPositions: {[key: string]: number} = {};
      for (let i = 1; i <= 4; i++) {
        const slotId = `tease_shot${i}`;
        const hOffsetKey = `tease_shot${i}_h_offset`;
        const hOffsetValue = newTeaseSettings[hOffsetKey] || '0';
        const position = parseFloat(hOffsetValue);
        if (!isNaN(position)) {
          newHorizontalPositions[slotId] = position;
        }
      }
      setVerticalVideoXPositions(newHorizontalPositions);
      console.log('[IntroOutro] Horizontal video positions loaded:', newHorizontalPositions);

      // Populate vertical video positions from v_offset values
      const newVerticalPositions: {[key: string]: number} = {};
      for (let i = 1; i <= 4; i++) {
        const slotId = `tease_shot${i}`;
        const vOffsetKey = `tease_shot${i}_v_offset`;
        const vOffsetValue = newTeaseSettings[vOffsetKey] || '0';
        const position = parseFloat(vOffsetValue);
        if (!isNaN(position)) {
          newVerticalPositions[slotId] = position;
        }
      }
      setVerticalVideoYPositions(newVerticalPositions);
      console.log('[IntroOutro] Vertical video positions loaded:', newVerticalPositions);

      // NOTE: we deliberately do NOT fall back to "a tease.wav exists at the
      // convention S3 path" here. Adopting a file purely because it sits at the
      // expected path is how a leftover / wrong-language tease leaks in: a
      // previous week (or the other language) can leave a tease.wav at a path
      // this week/language would look up, and it gets silently used. The tease
      // VO is authoritative ONLY from this week's settings for THIS language
      // (tease_intro_voiceover_url[_<lang>], set when the tease is generated).
      // If there's no setting, this week has no tease — full stop.

      // Check for the monolithic processed audio URL (was M14) — set by VO Manager
      // when it uploads a file. It's a week-level field already loaded above.
      try {
        const audioUrl = settingsResp.processedVoUrl;
        if (audioUrl && audioUrl.trim()) {
          console.log('[IntroOutro] Found processed audio URL:', audioUrl);
          setProcessedAudioUrl(audioUrl.trim());
        } else {
          console.log('[IntroOutro] No processed audio URL found');
          setProcessedAudioUrl(null);
        }
      } catch (audioError) {
        console.error('[IntroOutro] Error fetching processed audio URL:', audioError);
        setProcessedAudioUrl(null);
      }

      // 3. Fetch current week's videos (typed chart positions 1..10)
      // The DB model has no separate weekly "Seek In" column — each position's
      // seek comes from its linked Master Video List entry (videos.seek_in), which
      // positions[].seekIn already carries. Keep the seek-column index at -1 so the
      // legacy weekly-mirror sync paths stay inert.
      setWeeklySheetSeekInColIndex(-1);
      try {
        console.log(`[IntroOutro] Fetching current week's videos (positions) for: ${weeklySheetId}`);
        const positions = await chartDataService.getPositions(weeklySheetId);

        if (positions && positions.length > 0) {
          const weekVideos: MasterVideoInfo[] = [];

          positions.forEach((p) => {
            const song = String(p.song ?? '').trim();
            const artist = String(p.artist ?? '').trim();
            const s3Url = String(p.s3Url ?? '').trim();
            const position = p.position;

            console.log(`[IntroOutro] Position ${position}: ${artist} - ${song}: ${s3Url}`);

            if (s3Url) {
              const trimmedUrl = s3Url.trim();
              // Seek comes from the linked Master Video List entry (positions[].seekIn).
              let seekIn = String(p.seekIn ?? '0').trim() || '0';

              // Fallback to the master map (by URL, then filename) if the position
              // didn't carry a seek — mirrors the previous Master List fallback.
              if (seekIn === '0') {
                console.log(`[IntroOutro] Seek time not set for ${song}. Checking Master List...`);
                const masterInfo = masterVideoMap.get(trimmedUrl);
                if (masterInfo && masterInfo.seekIn && masterInfo.seekIn !== '0') {
                  seekIn = masterInfo.seekIn;
                  console.log(`[IntroOutro] Found Master List seek time match for ${song}: ${seekIn}`);
                } else {
                  // Try filename matching in master map as a further fallback
                  const extractFilename = (url: string): string => url.split('/').pop()?.split('?')[0]?.toLowerCase() || '';
                  const urlFilename = extractFilename(trimmedUrl);
                  const matchingMasterEntry = Array.from(masterVideoMap.values()).find((info: MasterVideoInfo) => extractFilename(info.s3Url) === urlFilename);

                  if (matchingMasterEntry && matchingMasterEntry.seekIn && matchingMasterEntry.seekIn !== '0') {
                    seekIn = matchingMasterEntry.seekIn;
                    console.log(`[IntroOutro] Found Master List filename seek time match for ${song}: ${seekIn}`);
                  } else {
                    console.log(`[IntroOutro] No valid seek time found for ${song} in Master List, defaulting to 0`);
                    // seekIn remains '0'
                  }
                }
              }

              weekVideos.push({ song, artist, s3Url, seekIn, position });

              // Debug for the specific problematic songs mentioned by user
              const problemSongs = ['luther', 'bar song', 'lose control', 'die with a smile'];
              if (problemSongs.some(problem => song.toLowerCase().includes(problem) || s3Url.toLowerCase().includes(problem.replace(/\s+/g, '')))) {
                console.log(`[IntroOutro DEBUG] Added problem song "${song}" to current week videos: seekIn = ${seekIn}, URL = ${s3Url}`);
              }
            }
          });

          setCurrentWeekVideos(weekVideos);
          // Save to local storage for future use
          saveCurrentWeekVideosToLocalStorage(weekVideos);

          console.log(`[IntroOutro] Current week videos loaded: ${weekVideos.length}`);
          console.log(`[IntroOutro] Current week video URLs with seek times:`,
            weekVideos.map(v => `${v.song} (${v.s3Url}): ${v.seekIn}`));
        } else {
          console.warn('[IntroOutro] No video data found in current week positions');
        }
      } catch (err) {
        console.error('[IntroOutro] Error loading current week videos:', err);
        // Don't set error state here - we'll continue even if this fails
      }

      // Set initial selected slot after data loads
      setSelectedSlotForSlider('video_left'); // Default to video_left initially

      // Sync outdated seek points after data loads successfully
      // Small delay to ensure state updates have completed
      setTimeout(() => {
        syncOutdatedSeekPoints().catch(console.error);
      }, 100);

    } catch (err) {
      console.error('[IntroOutro] Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load page data.');
    } finally {
      setLoading(false);
    }
  }, [weeklySheetId, saveCurrentWeekVideosToLocalStorage, loadCurrentWeekVideosFromLocalStorage, syncWeeklyDataToRenderSheet, location.state?.sheetName, masterVideoMap, setError, setLoading, setChartDate, syncOutdatedSeekPoints, extractDateFromSheet, formatSheetDate, currentDateString]);

  // Store the loadData function in the ref to break the circular dependency
  useEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);

  useEffect(() => {
    if (loadDataRef.current) {
      loadDataRef.current();
    }
  }, []);

  // --- Effect to update slider value when selected slot or settings change ---
  useEffect(() => {
    if (!selectedSlotForSlider) return;

    let currentSeekString: string | undefined;
    if (selectedSlotForSlider.startsWith('carousel') || selectedSlotForSlider === 'video_left') {
      currentSeekString = (introSettings as any)[`${selectedSlotForSlider}_seek`];
    } else if (selectedSlotForSlider.startsWith('tease_shot')) {
      currentSeekString = (teaseSettings as any)[`${selectedSlotForSlider}_seek`];
    } else if (selectedSlotForSlider.startsWith('outro')) {
      currentSeekString = (outroSettings as any)[`${selectedSlotForSlider}_seek`];
    }

    const currentSeekNumber = parseFloat(currentSeekString || '0');
    setSliderValue(isNaN(currentSeekNumber) ? 0 : currentSeekNumber);
    setTimeInputValue((isNaN(currentSeekNumber) ? 0 : currentSeekNumber).toFixed(2));

    // Update video duration for the slider maximum
    const updateDuration = () => {
      const duration = getVideoDurationForSlot(selectedSlotForSlider);
      setVideoDuration(duration);
      console.log(`[IntroOutro] Updated video duration for ${selectedSlotForSlider}: ${duration}s`);
    };
    
    // Try to get duration immediately, or wait for video to load
    setTimeout(updateDuration, 100); // Small delay to ensure video element exists
    setTimeout(updateDuration, 1000); // Fallback in case metadata loads later

  }, [selectedSlotForSlider, introSettings, outroSettings, teaseSettings, getVideoDurationForSlot]);

  // Add this useEffect to update the text input value when slider value changes
  useEffect(() => {
    setTimeInputValue(sliderValue.toFixed(2));
  }, [sliderValue]);

  // --- Event Handlers ---
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Reset selected slot when tab changes - accounting for tease tab
    let defaultSlot: string;
    const newTabType = getTabType(newValue);
    if (newTabType === 'tease') {
      defaultSlot = 'tease_shot1';
    } else if (newTabType === 'intro') {
      defaultSlot = 'video_left'; // Use video_left as default for intro
    } else {
      defaultSlot = 'outro_video1';
    }
    setSelectedSlotForSlider(defaultSlot);
    // Reset dynamic layout when tab changes to ensure correct base is used for pixel conversion
    setDynamicLayoutConfig(currentFormat === 'horizontal' ? HORIZONTAL_DEFAULT_LAYOUT : VERTICAL_DEFAULT_LAYOUT);
  };

  // --- TODO: Implement player click handler ---
  const handlePlayerClick = (slotId: string) => {
    if (dialogOpen) return; // Don't open dialog if already open
    console.log(`Player clicked: ${slotId}`);
    setSelectedSlot(slotId);
    setDialogOpen(true);
  };

  // --- TODO: Implement dialog close and save handlers ---
  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSlot(null);
  };

  // Update handleSaveVideoSelection to use cached videos first when finding seek values
  const handleSaveVideoSelection = useCallback(async (
    slotId: string,
    newS3Url: string,
    seekValue: string // seekValue from dialog is now ignored for lookup, but kept for reference/future use
  ) => {
    if (!weeklySheetId) return;

    console.log(`[IntroOutro] SAVING NEW VIDEO: Slot=${slotId}, URL=${newS3Url}, Original SeekValue from Dialog=${seekValue}`);

    try {
      // Determine if this is an intro, outro, or tease slot
      const isIntro = slotId.startsWith('carousel') || slotId === 'video_left';
      const isTease = slotId.startsWith('tease_shot');

      // Get the settings keys for the slot
      const keys = getKeysForSlot(slotId, activeVoLang);
      if (!keys) {
        console.error(`[IntroOutro] Invalid slot ID: ${slotId}`);
        return;
      }

      // --- Simplified Seek Value Logic - Use Master Video List as single source of truth ---
      let finalSeekValue = '0'; // Default to '0'

      // First try to find the video in master video map (single source of truth)
      const masterVideo = masterVideoMap.get(newS3Url);
      if (masterVideo && masterVideo.seekIn) {
        finalSeekValue = masterVideo.seekIn;
        console.log(`[IntroOutro] Using seek value from Master Video List: ${finalSeekValue} for ${masterVideo.song} by ${masterVideo.artist}`);
      } else {
        // Fallback: check currentWeekVideos for filename match (in case URL changed but it's the same video)
        const extractFilename = (url: string): string => {
          return url.split('/').pop()?.split('?')[0]?.toLowerCase() || '';
        };

        const urlFilename = extractFilename(newS3Url);
        const matchingVideo = currentWeekVideos.find(video => {
          const videoFilename = extractFilename(video.s3Url);
          return videoFilename === urlFilename;
        });

        if (matchingVideo && matchingVideo.seekIn && matchingVideo.seekIn !== '0') {
          finalSeekValue = matchingVideo.seekIn;
          console.log(`[IntroOutro] Using seek value from weekly videos (filename match): ${finalSeekValue} for ${matchingVideo.song}`);
        } else {
          console.log(`[IntroOutro] Video not found in Master Video List or weekly videos for ${newS3Url}. Using default seek time of 0.`);
        }
      }
      // --- End Simplified Seek Value Logic ---


      console.log(`[IntroOutro] Using final seek value: ${finalSeekValue} for ${slotId}`);

      // Prepare settings writes (url + seek, plus duration for tease shots)
      const writes: Promise<void>[] = [
        chartDataService.setSetting(weeklySheetId, keys.urlKey, newS3Url)
      ];

      // Always update seek with the final seek value
      if (keys.seekKey) {
        writes.push(chartDataService.setSetting(weeklySheetId, keys.seekKey, finalSeekValue));
      }

      // For tease shots, also initialize duration if not set
      if (isTease && keys.durationKey) {
        const currentDuration = teaseSettings[`${slotId}_duration`] || '5';
        writes.push(chartDataService.setSetting(weeklySheetId, keys.durationKey, currentDuration));
      }

      console.log(`[IntroOutro] Persisting slot settings for ${slotId}`);

      // Persist the settings writes
      await Promise.all(writes);

      // Then update the local state after spreadsheet update is successful
      const newData = {
        [`${slotId}_url`]: newS3Url,
        [`${slotId}_seek`]: finalSeekValue,
        ...(isTease && { [`${slotId}_duration`]: teaseSettings[`${slotId}_duration`] || '5' })
      };

      if (isIntro) {
        setIntroSettings(prev => ({
          ...prev,
          ...newData
        }));
      } else if (isTease) {
        setTeaseSettings(prev => ({
          ...prev,
          ...newData
        }));
      } else {
        setOutroSettings(prev => ({
          ...prev,
          ...newData
        }));
      }

      // Close dialogs
      setDialogOpen(false);
      setSelectedSlot(null);

      // Also update selected slot for slider and slider value
      setSelectedSlotForSlider(slotId);
      const seekNumber = parseFloat(finalSeekValue);
      setSliderValue(isNaN(seekNumber) ? 0 : seekNumber);
      setTimeInputValue((isNaN(seekNumber) ? 0 : seekNumber).toFixed(2));

      console.log(`[IntroOutro] Successfully saved video selection for ${slotId}`);
    } catch (error) {
      console.error('[IntroOutro] Error saving video selection:', error);
    }
  }, [weeklySheetId, currentWeekVideos, masterVideoMap]); // Added masterVideoMap back as it's used in the function

  // Modified handler for slot selection change
  const handleSlotSelectionChange = (event: SelectChangeEvent<string>) => {
    const selectedSlot = event.target.value as string;
    setSelectedSlotForSlider(selectedSlot);
    
    // Explicitly update slider value based on the selected slot
    let seekValue: string | undefined;
    
    if (selectedSlot.startsWith('carousel') || selectedSlot === 'video_left') {
      seekValue = (introSettings as any)[`${selectedSlot}_seek`];
    } else if (selectedSlot.startsWith('tease_shot')) {
      seekValue = (teaseSettings as any)[`${selectedSlot}_seek`];
    } else if (selectedSlot.startsWith('outro')) {
      seekValue = (outroSettings as any)[`${selectedSlot}_seek`];
    }
    
    // No need for special handling of video_left anymore since it now has a seek time
    // Parse and set the slider value
    const parsedValue = parseFloat(seekValue || '0');
    setSliderValue(isNaN(parsedValue) ? 0 : parsedValue);
    setTimeInputValue((isNaN(parsedValue) ? 0 : parsedValue).toFixed(2));
    
    console.log(`[IntroOutro] Selected slot ${selectedSlot} with seek value ${seekValue || '0'}`);
  };

  // Modified slider change handler to update preview state while dragging
  // Add a debounce timer ref
  const sliderDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Add debounce timer refs for positioning sliders
  const horizontalPositionDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const verticalPositionDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  
  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setSliderValue(newValue as number);
    setIsDraggingSlider(true); // Set dragging flag when slider is being moved
    
    // Clear any existing debounce timer
    if (sliderDebounceTimer.current) {
      clearTimeout(sliderDebounceTimer.current);
    }
    
    // Set a new debounce timer to save after 500ms of no changes
    sliderDebounceTimer.current = setTimeout(() => {
      console.log('🎯 [DEBOUNCE SAVE] Saving after debounce, value:', newValue);
      handleSliderChangeCommitted({} as React.SyntheticEvent, newValue);
    }, 500);
  };

  // Debounced handlers for positioning sliders
  const handleHorizontalPositionChange = (newValue: number) => {
    // Update local state immediately for responsive UI
    setVerticalVideoXPositions(prev => ({
      ...prev,
      [selectedSlotForSlider]: newValue
    }));

    // Clear any existing debounce timer
    if (horizontalPositionDebounceTimer.current) {
      clearTimeout(horizontalPositionDebounceTimer.current);
    }

    // Set a new debounce timer to save after 500ms of no changes
    horizontalPositionDebounceTimer.current = setTimeout(() => {
      console.log('🎯 [HORIZONTAL DEBOUNCE] Saving horizontal position after debounce, value:', newValue);
      saveHorizontalPositionToSheet(selectedSlotForSlider, newValue);
    }, 500);
  };

  const handleVerticalPositionChange = (newValue: number) => {
    // Update local state immediately for responsive UI
    setVerticalVideoYPositions(prev => ({
      ...prev,
      [selectedSlotForSlider]: newValue
    }));

    // Clear any existing debounce timer
    if (verticalPositionDebounceTimer.current) {
      clearTimeout(verticalPositionDebounceTimer.current);
    }

    // Set a new debounce timer to save after 500ms of no changes
    verticalPositionDebounceTimer.current = setTimeout(() => {
      console.log('🎯 [VERTICAL DEBOUNCE] Saving vertical position after debounce, value:', newValue);
      saveVerticalPositionToSheet(selectedSlotForSlider, newValue);
    }, 500);
  };

  // Modified slider change committed handler to save immediately
  const handleSliderChangeCommitted = useCallback(async (event: React.SyntheticEvent | Event, newValue: number | number[]) => {
    console.log('🎯 [SEEK DEBUG] ==================== SLIDER CHANGE COMMITTED ====================');
    console.log('🎯 [SEEK DEBUG] handleSliderChangeCommitted CALLED!');
    console.log('🎯 [SEEK DEBUG] Event type:', event?.type || 'unknown');
    console.log('🎯 [SEEK DEBUG] New value:', newValue);
    console.log('🎯 [SEEK DEBUG] Current state:', {
      weeklySheetId,
      selectedSlotForSlider,
      currentSliderValue: sliderValue,
      isDraggingSlider,
      isUpdatingSheet
    });
    
    setIsDraggingSlider(false);

    if (!weeklySheetId || !selectedSlotForSlider) {
      console.error('🎯 [SEEK DEBUG] ❌ Missing required data:', { weeklySheetId, selectedSlotForSlider });
      return;
    }

    const newSeekTime = newValue as number;
    const oldSeekTime = sliderValue; // Current slider value before change
    
    console.log('🎯 [SEEK DEBUG] Value comparison:', { 
      newSeekTime, 
      oldSeekTime, 
      difference: Math.abs(newSeekTime - oldSeekTime),
      threshold: 0.01 
    });
    
    // Check if there was actually a change
    if (Math.abs(newSeekTime - oldSeekTime) < 0.01) {
      console.log('🎯 [SEEK DEBUG] ⚠️ No significant change detected, aborting save');
      return; // No significant change
    }

    const keys = getKeysForSlot(selectedSlotForSlider, activeVoLang);
    console.log('🎯 [SEEK DEBUG] Settings key mapping for slot:', { selectedSlotForSlider, keys });

    if (!keys || !keys.seekKey) {
      console.error('🎯 [SEEK DEBUG] ❌ Seek key not found for slot:', selectedSlotForSlider);
      setError(`Cannot update seek time for ${selectedSlotForSlider}.`);
      return;
    }

    // Get current video info
    const isIntro = selectedSlotForSlider.startsWith('carousel') || selectedSlotForSlider === 'video_left';
    const isTease = selectedSlotForSlider.startsWith('tease_shot');
    
    let currentSettings: any;
    if (isTease) {
      currentSettings = settingsRef.current.teaseSettings;
    } else if (isIntro) {
      currentSettings = settingsRef.current.introSettings;
    } else {
      currentSettings = settingsRef.current.outroSettings;
    }
    
    const currentUrl = (currentSettings as any)[`${selectedSlotForSlider}_url`] || '';
    
    console.log('🎯 [SEEK DEBUG] Video info retrieval:', {
      isIntro,
      isTease,
      currentUrl,
      settingsRef: {
        intro: Object.keys(settingsRef.current.introSettings).filter(k => k.includes(selectedSlotForSlider)),
        outro: Object.keys(settingsRef.current.outroSettings).filter(k => k.includes(selectedSlotForSlider)),
        tease: Object.keys(settingsRef.current.teaseSettings).filter(k => k.includes(selectedSlotForSlider))
      }
    });

    if (!currentUrl) {
      console.error('🎯 [SEEK DEBUG] ❌ Could not find current URL for slot:', selectedSlotForSlider);
      console.error('🎯 [SEEK DEBUG] Available settings:', currentSettings);
      setError('Could not find the video URL to save. Please try setting the video again.');
      return;
    }

    // Find video info to get song/artist names
    const videoInfo = currentWeekVideos.find(v => v.s3Url === currentUrl) || 
                     Array.from(masterVideoMap.values()).find(v => v.s3Url === currentUrl);

    console.log('🎯 [SEEK DEBUG] Video info lookup:', {
      currentUrl,
      videoInfo: videoInfo ? { song: videoInfo.song, artist: videoInfo.artist } : null,
      currentWeekVideosCount: currentWeekVideos.length,
      masterVideoMapSize: masterVideoMap.size
    });

    // Save immediately and sync everywhere
    console.log('🎯 [SEEK DEBUG] ✅ Proceeding to save seek time:', {
      newSeekTime,
      selectedSlotForSlider,
      currentUrl,
      videoTitle: videoInfo ? `${videoInfo.song} by ${videoInfo.artist}` : 'Unknown Video'
    });
    
    const videoTitle = videoInfo ? `${videoInfo.song} by ${videoInfo.artist}` : 'Unknown Video';
    if (executeSeekTimeSaveRef.current) {
      console.log('🎯 [SEEK DEBUG] Calling executeSeekTimeSave...');
      await executeSeekTimeSaveRef.current(newSeekTime, currentUrl, videoTitle);
      console.log('🎯 [SEEK DEBUG] executeSeekTimeSave completed');
    } else {
      console.error('🎯 [SEEK DEBUG] ❌ executeSeekTimeSaveRef.current is null!');
    }
  }, [weeklySheetId, selectedSlotForSlider, sliderValue, currentWeekVideos, masterVideoMap, isDraggingSlider, isUpdatingSheet]);

  // Helper function to find video info by URL
  const findVideoByUrl = useCallback((url: string): MasterVideoInfo | undefined => {
    // First check currentWeekVideos
    const weeklyVideo = currentWeekVideos.find(v => v.s3Url === url);
    if (weeklyVideo) return weeklyVideo;
    
    // Then check masterVideoMap
    return Array.from(masterVideoMap.values()).find(v => v.s3Url === url);
  }, [currentWeekVideos, masterVideoMap]);

  // Helper function to update Master Video List seek time
  const updateMasterVideoListSeekTime = useCallback(async (videoUrl: string, newSeekTime: number): Promise<boolean> => {
    console.log('🔍 [MASTER DEBUG] ==================== UPDATE MASTER VIDEO LIST ====================');
    console.log('🔍 [MASTER DEBUG] Input parameters:', {
      videoUrl,
      newSeekTime,
      masterVideoListId: MASTER_VIDEO_LIST_ID
    });

    try {
      // Find the video in the master map to get song/artist info
      const videoInfo = findVideoByUrl(videoUrl);
      console.log('🔍 [MASTER DEBUG] Video info lookup result:', videoInfo);
      
      if (!videoInfo) {
        console.error('🔍 [MASTER DEBUG] ❌ Video not found in master map:', videoUrl);
        console.error('🔍 [MASTER DEBUG] Available videos in master map:', Array.from(masterVideoMap.keys()).slice(0, 5));
        return false;
      }

      // Update the seek time on the matching Master Video List entry (videos table)
      // by its (song, artist) key — the typed, Sheets-free path.
      console.log('🔍 [MASTER DEBUG] Updating Master Video List seek via updateVideoByKey:', {
        song: videoInfo.song,
        artist: videoInfo.artist,
        newValue: newSeekTime.toString()
      });

      const updatedVideo = await chartDataService.updateVideoByKey(
        videoInfo.song,
        videoInfo.artist,
        { seekIn: newSeekTime.toString() }
      );

      if (!updatedVideo) {
        console.error('🔍 [MASTER DEBUG] ❌ updateVideoByKey returned no video for:', videoInfo.song, videoInfo.artist);
        return false;
      }
      console.log('🔍 [MASTER DEBUG] ✅ Master Video List update completed');

      // Update local master video map
      console.log('🔍 [MASTER DEBUG] Updating local master video map...');
      setMasterVideoMap(prev => {
        const newMap = new Map(prev);
        const existing = newMap.get(videoUrl);
        if (existing) {
          console.log('🔍 [MASTER DEBUG] Found existing entry in local map:', existing);
          newMap.set(videoUrl, { ...existing, seekIn: newSeekTime.toString() });
          console.log('🔍 [MASTER DEBUG] Updated local map entry:', { ...existing, seekIn: newSeekTime.toString() });
        } else {
          console.log('🔍 [MASTER DEBUG] ⚠️ No existing entry found in local map for URL:', videoUrl);
        }
        return newMap;
      });

      console.log(`🔍 [MASTER DEBUG] ✅ Successfully updated Master Video List seek time for ${videoInfo.song} to ${newSeekTime}s`);
      return true;
    } catch (error) {
      console.error('🔍 [MASTER DEBUG] ❌ Error updating Master Video List:', error);
      console.error('🔍 [MASTER DEBUG] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace',
        videoUrl,
        newSeekTime
      });
      return false;
    }
  }, [findVideoByUrl, masterVideoMap, setMasterVideoMap]);

  // Helper function to sync seek time across all carousel positions
  const syncSeekTimeAcrossPositions = useCallback(async (videoUrl: string, newSeekTime: number, excludeSlot?: string): Promise<number> => {
    const allSlots = ['carousel_video1', 'carousel_video2', 'carousel_video3', 'carousel_video4', 'video_left', 'outro_video1', 'outro_video2', 'outro_video3', 'outro_video4', 'outro_video5', 'outro_video6'];
    const slotsToSync: { key: string; value: string }[] = [];
    let syncedCount = 0;

    for (const slotId of allSlots) {
      if (slotId === excludeSlot) continue; // Skip the slot that triggered the change

      const isSlotIntro = slotId.startsWith('carousel') || slotId === 'video_left';
      const slotSettings = isSlotIntro ? settingsRef.current.introSettings : settingsRef.current.outroSettings;
      const slotUrl = (slotSettings as any)[`${slotId}_url`] || '';

      // If this slot uses the same URL, sync the seek time
      if (slotUrl === videoUrl) {
        const slotKeys = getKeysForSlot(slotId, activeVoLang);
        if (slotKeys?.seekKey) {
          console.log(`[IntroOutro] Syncing seek time ${newSeekTime} to ${slotId} (same video)`);
          slotsToSync.push({ key: slotKeys.seekKey, value: newSeekTime.toString() });
          syncedCount++;

          // Update local state for this slot too
          if (isSlotIntro) {
            setIntroSettings(prev => ({ ...prev, [`${slotId}_seek`]: newSeekTime.toString() }));
          } else {
            setOutroSettings(prev => ({ ...prev, [`${slotId}_seek`]: newSeekTime.toString() }));
          }
        }
      }
    }

    // The current-week videos derive their seek from the linked Master Video List
    // entry (updated separately via updateMasterVideoListSeekTime), so there is no
    // separate weekly seek column to mirror here in the DB model.

    // Persist all the per-slot seek settings writes together
    if (slotsToSync.length > 0) {
      await Promise.all(slotsToSync.map(s => chartDataService.setSetting(weeklySheetId, s.key, s.value)));
    }

    return syncedCount;
  }, [weeklySheetId, setIntroSettings, setOutroSettings]);

  // Function to actually save seek time changes
  const executeSeekTimeSave = useCallback(async (seekValueToSave: number, currentUrl: string, videoTitle: string) => {
    console.log('📝 [SAVE DEBUG] ==================== EXECUTE SEEK TIME SAVE ====================');
    console.log('📝 [SAVE DEBUG] Input parameters:', {
      seekValueToSave,
      currentUrl,
      videoTitle,
      weeklySheetId,
      selectedSlotForSlider
    });

    if (!weeklySheetId || !selectedSlotForSlider) {
      console.error('📝 [SAVE DEBUG] ❌ Missing required parameters');
      return;
    }

    const keys = getKeysForSlot(selectedSlotForSlider, activeVoLang);
    console.log('📝 [SAVE DEBUG] Settings key mapping result:', keys);

    if (!keys || !keys.seekKey) {
      console.error('📝 [SAVE DEBUG] ❌ Cannot get settings keys for slot:', selectedSlotForSlider);
      setError(`Cannot update seek time for ${selectedSlotForSlider}.`);
      return;
    }

    console.log('📝 [SAVE DEBUG] Save operation details:', {
      slot: selectedSlotForSlider,
      urlKey: keys.urlKey,
      seekKey: keys.seekKey,
      newSeekValue: seekValueToSave,
      currentUrl,
      videoTitle
    });

    setIsUpdatingSheet(true);
    setError(null);

    try {
      const isIntro = selectedSlotForSlider.startsWith('carousel') || selectedSlotForSlider === 'video_left';
      const isTease = selectedSlotForSlider.startsWith('tease_shot');
      console.log('📝 [SAVE DEBUG] Determined slot type:', { isIntro, isTease });

      // Always update the current slot first (seek + url settings keys)
      console.log('📝 [SAVE DEBUG] About to persist slot settings...');
      await Promise.all([
        chartDataService.setSetting(weeklySheetId, keys.seekKey, seekValueToSave.toString()),
        chartDataService.setSetting(weeklySheetId, keys.urlKey, currentUrl),
      ]);
      console.log('📝 [SAVE DEBUG] ✅ Slot settings persisted');

      // Update local state for the current slot
      console.log('📝 [SAVE DEBUG] Updating local state...');
      if (isIntro) {
        console.log('📝 [SAVE DEBUG] Updating intro settings for:', selectedSlotForSlider);
        setIntroSettings(prev => ({ ...prev, [`${selectedSlotForSlider}_seek`]: seekValueToSave.toString(), [`${selectedSlotForSlider}_url`]: currentUrl }));
      } else if (isTease) {
        console.log('📝 [SAVE DEBUG] Updating tease settings for:', selectedSlotForSlider);
        setTeaseSettings(prev => ({ ...prev, [`${selectedSlotForSlider}_seek`]: seekValueToSave.toString(), [`${selectedSlotForSlider}_url`]: currentUrl }));
      } else {
        console.log('📝 [SAVE DEBUG] Updating outro settings for:', selectedSlotForSlider);
        setOutroSettings(prev => ({ ...prev, [`${selectedSlotForSlider}_seek`]: seekValueToSave.toString(), [`${selectedSlotForSlider}_url`]: currentUrl }));
      }

      // Always update Master Video List and sync everywhere
      console.log('📝 [SAVE DEBUG] About to update Master Video List...');
      const masterUpdateSuccess = await updateMasterVideoListSeekTime(currentUrl, seekValueToSave);
      console.log('📝 [SAVE DEBUG] Master Video List update result:', masterUpdateSuccess);

      // Sync across all carousel positions (excluding the current one)
      console.log('📝 [SAVE DEBUG] About to sync seek time across positions...');
      const syncedCount = await syncSeekTimeAcrossPositions(currentUrl, seekValueToSave, selectedSlotForSlider);
      console.log('📝 [SAVE DEBUG] Synced across positions count:', syncedCount);
      
      let message = `Updated seek point for ${videoTitle}`;
      if (masterUpdateSuccess) {
        if (syncedCount > 0) {
          message += ` in Master List + ${syncedCount} position${syncedCount > 1 ? 's' : ''}`;
        } else {
          message += ` in Master List`;
        }
        enqueueSnackbar(message, { variant: 'success' });
      } else {
        const warningMessage = `Updated seek point for ${videoTitle} (Master List sync failed)`;
        enqueueSnackbar(warningMessage, { variant: 'warning' });
      }

      console.log('📝 [SAVE DEBUG] ✅ Seek time updated successfully');
    } catch (err) {
      console.error('📝 [SAVE DEBUG] ❌ ERROR in executeSeekTimeSave:', err);
      console.error('📝 [SAVE DEBUG] Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : 'No stack trace'
      });
      setError(err instanceof Error ? err.message : 'Failed to update seek time.');
    } finally {
      console.log('📝 [SAVE DEBUG] Setting isUpdatingSheet to false');
      setIsUpdatingSheet(false);
    }
  }, [weeklySheetId, selectedSlotForSlider, findVideoByUrl, updateMasterVideoListSeekTime, syncSeekTimeAcrossPositions, setIntroSettings, setOutroSettings, enqueueSnackbar, setIsUpdatingSheet, setError]);

  // Store the executeSeekTimeSave function in the ref to break circular dependency
  useEffect(() => {
    executeSeekTimeSaveRef.current = executeSeekTimeSave;
  }, [executeSeekTimeSave]);

  // Add handler for direct time input change
  const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTimeInputValue(e.target.value);
  };

  // Add handler for when time input is submitted
  const handleTimeInputBlur = () => {
    console.log('⌨️ [INPUT DEBUG] Time input blur triggered:', {
      timeInputValue,
      currentSliderValue: sliderValue,
      videoDuration,
      isUpdatingSheet
    });
    
    // Parse the input value and validate
    const parsedValue = parseFloat(timeInputValue);
    console.log('⌨️ [INPUT DEBUG] Parsed value:', parsedValue);
    
    if (isNaN(parsedValue) || parsedValue < 0 || parsedValue > videoDuration) {
      console.log('⌨️ [INPUT DEBUG] Invalid value, resetting to slider value');
      // Reset to current slider value if invalid or out of range
      setTimeInputValue(sliderValue.toFixed(2));
      return;
    }
    
    // Update slider value
    console.log('⌨️ [INPUT DEBUG] Updating slider value to:', parsedValue);
    setSliderValue(parsedValue);
    
    // If not currently updating, save the value
    if (!isUpdatingSheet) {
      console.log('⌨️ [INPUT DEBUG] Triggering slider change committed for manual input');
      handleSliderChangeCommitted({} as React.SyntheticEvent, parsedValue);
    } else {
      console.log('⌨️ [INPUT DEBUG] Sheet is currently updating, skipping save');
    }
  };

  // Add handler for key press in time input
  const handleTimeInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // Trigger the blur event to save
    }
  };

  // --- Draggable and Resizable Handlers --- (Temporarily disabled)
  // const handleDragStop = (slotId: string, e: DraggableEvent, data: DraggableData) => {
  //   setDynamicLayoutConfig(prevConfig => ({
  //     ...prevConfig,
  //     [slotId]: {
  //       ...prevConfig[slotId],
  //       top: `${data.y}px`,
  //       left: `${data.x}px`,
  //     }
  //   }));
  // };

  // const handleResizeStop = (slotId: string, e: React.SyntheticEvent, data: { size: { width: number; height: number } }) => {
  //   setDynamicLayoutConfig(prevConfig => ({
  //     ...prevConfig,
  //     [slotId]: {
  //       ...prevConfig[slotId],
  //       width: `${data.size.width}px`,
  //       height: `${data.size.height}px`,
  //     }
  //   }));
  // };

  // Helper functions to determine active tab content (accounting for dynamic Tease tab)
  const getTabType = useCallback((tabIndex: number): 'tease' | 'intro' | 'outro' => {
    if (hasTeaseText) {
      // When tease tab exists: Tease=0, Intro=1, Outro=2
      if (tabIndex === 0) return 'tease';
      if (tabIndex === 1) return 'intro';
      return 'outro';
    } else {
      // When no tease tab: Intro=0, Outro=1
      if (tabIndex === 0) return 'intro';
      return 'outro';
    }
  }, [hasTeaseText]);

  const isTeaseActive = getTabType(activeTab) === 'tease';
  const isIntroActive = getTabType(activeTab) === 'intro';
  const isOutroActive = getTabType(activeTab) === 'outro';

  // Add an effect to debug the background image issues
  useEffect(() => {
    const tabType = getTabType(activeTab);
    if (tabType === 'tease') {
      console.log(`Tease tab active - no background image, using tease border overlay on video players`);
    } else if (tabType === 'intro') {
      console.log(`Showing intro background image: ${process.env.PUBLIC_URL}/intro.jpg`);
    } else {
      console.log(`Showing outro background image: ${process.env.PUBLIC_URL}/outro.jpg`);
    }
  }, [activeTab, hasTeaseText, getTabType]);

  // Refresh Tease VO URL from S15 when Tease tab is opened
  useEffect(() => {
    const fetchTeaseDataFromSheet = async () => {
      if (!isTeaseActive || !weeklySheetId) return;

      try {
        console.log('[IntroOutro] Tease tab opened, fetching latest Tease settings...');

        // Fetch the line-specific tease VO (was S15) + all tease shot settings
        const [voResp, settingsResp] = await Promise.all([
          chartDataService.getVO(weeklySheetId, getVoLanguage(weeklySheetId)),
          chartDataService.getSettings(weeklySheetId),
        ]);
        const lineVoUrl = voResp.lineVoUrls?.['Tease'] || '';
        const settingsValues = settingsResp.settings || {};

        // Helper to read a tease setting by key
        const getTeaseSetting = (key: string): string | undefined => {
          const value = settingsValues[key];
          const out = (value === undefined || value === null || value === '') ? undefined : String(value);
          console.log(`[IntroOutro Tease Refresh] ${key} = ${out ?? 'undefined'}`);
          return out;
        };

        // Build updated tease settings
        const updatedSettings: any = {};

        // Shot 1
        updatedSettings.tease_shot1_url = getTeaseSetting('tease_shot1_url') || '';
        updatedSettings.tease_shot1_seek = getTeaseSetting('tease_shot1_seek') || '0';
        updatedSettings.tease_shot1_duration = getTeaseSetting('tease_shot1_duration') || '5';
        updatedSettings.tease_shot1_h_offset = getTeaseSetting('tease_shot1_h_offset') || '0';
        updatedSettings.tease_shot1_v_offset = getTeaseSetting('tease_shot1_v_offset') || '0';

        // Shot 2
        updatedSettings.tease_shot2_url = getTeaseSetting('tease_shot2_url') || '';
        updatedSettings.tease_shot2_seek = getTeaseSetting('tease_shot2_seek') || '0';
        updatedSettings.tease_shot2_duration = getTeaseSetting('tease_shot2_duration') || '5';
        updatedSettings.tease_shot2_h_offset = getTeaseSetting('tease_shot2_h_offset') || '0';
        updatedSettings.tease_shot2_v_offset = getTeaseSetting('tease_shot2_v_offset') || '0';

        // Shot 3
        updatedSettings.tease_shot3_url = getTeaseSetting('tease_shot3_url') || '';
        updatedSettings.tease_shot3_seek = getTeaseSetting('tease_shot3_seek') || '0';
        updatedSettings.tease_shot3_duration = getTeaseSetting('tease_shot3_duration') || '5';
        updatedSettings.tease_shot3_h_offset = getTeaseSetting('tease_shot3_h_offset') || '0';
        updatedSettings.tease_shot3_v_offset = getTeaseSetting('tease_shot3_v_offset') || '0';

        // Shot 4
        updatedSettings.tease_shot4_url = getTeaseSetting('tease_shot4_url') || '';
        updatedSettings.tease_shot4_seek = getTeaseSetting('tease_shot4_seek') || '0';
        updatedSettings.tease_shot4_duration = getTeaseSetting('tease_shot4_duration') || '5';
        updatedSettings.tease_shot4_h_offset = getTeaseSetting('tease_shot4_h_offset') || '0';
        updatedSettings.tease_shot4_v_offset = getTeaseSetting('tease_shot4_v_offset') || '0';

        // Update VO URL if present
        if (lineVoUrl && lineVoUrl.trim()) {
          console.log('[IntroOutro] Found line-specific Tease VO:', lineVoUrl);
          updatedSettings.tease_intro_voiceover_url = lineVoUrl.trim();
        }

        console.log('[IntroOutro] Updating tease settings with fresh data from sheet:', updatedSettings);
        setTeaseSettings(prev => ({
          ...prev,
          ...updatedSettings
        }));
      } catch (error) {
        console.error('[IntroOutro] Error fetching Tease data from sheet:', error);
      }
    };

    fetchTeaseDataFromSheet();
  }, [isTeaseActive, weeklySheetId]);


  // Handle navigation with save
  const handleReturnToCharts = () => {
    // Seek times are already persisted to the Master Video List at edit time
    // (executeSeekTimeSave → updateMasterVideoListSeekTime), so there's nothing to
    // flush on the way out. The old bulk saveMasterSeekTimes() re-read seek from a
    // weekly column that doesn't hold seek (col P = "Line VO dB"; in DB mode that
    // cell is empty → '0'), pushing garbage/0 into every matched video's Master
    // seek. Removed — just navigate. (Bug was present in Sheets mode too.)
    navigate('/');
  };

  // Modify the delayed cleanup useEffect to ensure it runs for all dates, even with no videos
  useEffect(() => {
    if (!loading) {
      // Create a hash of current videos to track if we've already cleaned up this set
      // If there are no videos, use a special key
      const videosHash = currentWeekVideos.length > 0 
        ? currentWeekVideos.map(v => v.s3Url).sort().join('|')
        : 'NO_VIDEOS';
      
      console.log(`[IntroOutro] Current videos hash: ${videosHash.substring(0, 40)}...`);
      console.log(`[IntroOutro] Already cleaned up? ${Boolean(cleanupRunRef.current[videosHash])}`);
      
      // Only run cleanup if we haven't already done it for this set of videos
      if (!cleanupRunRef.current[videosHash]) {
        // Add a delay to ensure videos are rendered before cleanup runs
        const cleanupTimer = setTimeout(() => {
          console.log('[IntroOutro] Running cleanup check after videos have rendered...');
          cleanupOutdatedVideos(currentWeekVideos);
          
          // Mark this set of videos as processed
          cleanupRunRef.current[videosHash] = true;
        }, 2000); // 2 second delay to ensure videos have loaded
        
        return () => clearTimeout(cleanupTimer);
      }
    }
  }, [currentWeekVideos, loading, cleanupOutdatedVideos]);

  // --- Render Logic ---
  if (!weeklySheetId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Weekly Sheet ID was not provided. Please navigate from the main charts page.</Alert>
        <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Go to Charts
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
  }

  // Helper to render players for a given section
  const renderPlayers = (settings: IntroSettings | OutroSettings | undefined, prefix: string, count: number, isLeft?: boolean) => {
    if (!settings) return []; // Add a guard for undefined settings

    const players = [];
    // Use dynamicLayoutConfig instead of static defaults
    // const activeLayout = currentFormat === 'horizontal' ? HORIZONTAL_DEFAULT_LAYOUT : VERTICAL_DEFAULT_LAYOUT;

    for (let i = 1; i <= count; i++) {
      const slotId = isLeft ? prefix : (prefix === 'tease_shot' ? `${prefix}${i}` : `${prefix}_video${i}`);
      
      if (currentFormat === 'vertical' && slotId === 'outro_video2') {
        continue;
      }

      let url = (settings as any)[`${slotId}_url`];
      const seek = (settings as any)[`${slotId}_seek`];
      
      // Debug logging for video_left
      if (slotId === 'video_left') {
        console.log(`[DEBUG] video_left slot:`, { 
          slotId, 
          url: url,
          seek, 
          settingsKeys: Object.keys(settings || {}),
          isLeft,
          prefix 
        });
        console.log(`[DEBUG] video_left full URL being passed to MiniVideoPlayer: "${url}"`);
      }
      
      const layout = dynamicLayoutConfig[slotId];
      const safeLayout = layout || { top: '0%', left: '0%', width: '150px', height: '84px'}; 

      // Debug logging for outro_video6
      if (slotId === 'outro_video6') {
        console.log(`[DEBUG] outro_video6 layout:`, layout);
        console.log(`[DEBUG] outro_video6 safeLayout:`, safeLayout);
        console.log(`[DEBUG] currentFormat:`, currentFormat);
        console.log(`[DEBUG] activeTab:`, activeTab);
      }

      const isPreviewSlot = isDraggingSlider && slotId === selectedSlotForSlider;
      let songInfo = null;
      if (url) {
        const trimmedUrl = url.trim(); // Ensure URL is trimmed
        console.log(`[IntroOutro] Looking up info for ${slotId}, URL: ${trimmedUrl}`);
        console.log(`[DEBUG] VIDEO LOOKUP CALLED - This should always show!`);
        
        // Check if this URL contains any of the problematic song names
        const problemSongs = ['luther', 'bar song', 'lose control', 'die with a smile'];
        const hasProblemSong = problemSongs.some(problem => trimmedUrl.toLowerCase().includes(problem.replace(/\s+/g, '')));
        if (hasProblemSong) {
          console.log(`[IntroOutro DEBUG] Slot ${slotId} contains problematic song URL: ${trimmedUrl}`);
        }
        
        // Helper function to extract and normalize filename
        const extractFilename = (fileUrl: string): string => {
          const filename = fileUrl.split('/').pop() || '';
          // Remove any query parameters
          return filename.split('?')[0].toLowerCase();
        };
        
        // Helper function to normalize S3 URLs (handle region differences, URL encoding, and dash spacing)
        const normalizeS3Url = (url: string): string => {
          const decoded = decodeURIComponent(url)
            .replace(/\.s3\.us-west-2\.amazonaws\.com/g, '.s3.amazonaws.com')
            .replace(/\.s3\.[^.]+\.amazonaws\.com/g, '.s3.amazonaws.com');
          
          // Only normalize dash spacing in the filename part (after last slash)
          const lastSlashIndex = decoded.lastIndexOf('/');
          if (lastSlashIndex !== -1) {
            const basePath = decoded.substring(0, lastSlashIndex + 1);
            const filename = decoded.substring(lastSlashIndex + 1);
            // Normalize dash spacing only in filename: convert " -" and "- " to " - "
            const normalizedFilename = filename.replace(/\s*-\s*/g, ' - ');
            return basePath + normalizedFilename;
          }
          
          return decoded.trim();
        };
        
        // Try to find exact URL match (with trimming and normalization)
        const normalizedTrimmedUrl = normalizeS3Url(trimmedUrl);
        console.log(`[DEBUG] Original URL: ${trimmedUrl}`);
        console.log(`[DEBUG] Normalized URL: ${normalizedTrimmedUrl}`);
        
        // Show ALL current week video URLs for comparison
        if (currentWeekVideos.length > 0) {
          console.log(`[DEBUG] ALL current week videos (${currentWeekVideos.length} total):`);
          currentWeekVideos.forEach((v, index) => {
            const normalizedVideo = normalizeS3Url(v.s3Url);
            const matches = normalizedVideo === normalizedTrimmedUrl;
            console.log(`  ${index + 1}. "${v.song}" by "${v.artist}"`);
            console.log(`     Original: ${v.s3Url}`);
            console.log(`     Normalized: ${normalizedVideo}`);
            console.log(`     Matches search URL: ${matches}`);
            
            // If URLs appear identical but don't match, do character-by-character comparison
            if (!matches && normalizedVideo === normalizedTrimmedUrl.toString()) {
              console.log(`     [CHAR DEBUG] Search URL length: ${normalizedTrimmedUrl.length}, Video URL length: ${normalizedVideo.length}`);
              console.log(`     [CHAR DEBUG] Search URL chars:`, normalizedTrimmedUrl.split('').map((c, i) => `${i}:${c}(${c.charCodeAt(0)})`));
              console.log(`     [CHAR DEBUG] Video URL chars:`, normalizedVideo.split('').map((c, i) => `${i}:${c}(${c.charCodeAt(0)})`));
              
              // Find first difference
              for (let i = 0; i < Math.max(normalizedTrimmedUrl.length, normalizedVideo.length); i++) {
                const searchChar = normalizedTrimmedUrl[i];
                const videoChar = normalizedVideo[i];
                if (searchChar !== videoChar) {
                  console.log(`     [CHAR DEBUG] First difference at position ${i}: search="${searchChar}"(${searchChar?.charCodeAt(0)}) vs video="${videoChar}"(${videoChar?.charCodeAt(0)})`);
                  break;
                }
              }
            }
          });
        }
        songInfo = Array.from(masterVideoMap.values()).find(info => 
          normalizeS3Url(info.s3Url) === normalizedTrimmedUrl
        );
        
        if (songInfo) {
          console.log(`[IntroOutro] Found exact URL match for ${slotId}: ${songInfo.song} - ${songInfo.artist}`);
        }
        
        // If not found, try to match filename
        if (!songInfo) {
          const urlFilename = extractFilename(trimmedUrl);
          console.log(`[IntroOutro] Trying filename match for ${slotId}, filename: ${urlFilename}`);
          
          songInfo = Array.from(masterVideoMap.values()).find(info => {
            const infoFilename = extractFilename(info.s3Url);
            const matches = urlFilename === infoFilename;
            if (matches) {
              console.log(`[IntroOutro] Found filename match for ${slotId}: ${info.song} - ${info.artist}`);
            }
            return matches;
          });
        }
        
        // If still not found, check current week videos
        if (!songInfo && currentWeekVideos.length > 0) {
          // Try exact URL match first (with normalization)
          songInfo = currentWeekVideos.find(info => 
            normalizeS3Url(info.s3Url) === normalizedTrimmedUrl
          );
          
          if (songInfo) {
            console.log(`[IntroOutro] Found exact URL match in current week videos for ${slotId}: ${songInfo.song} - ${songInfo.artist}`);
          }
          
          // Try filename match with current week videos
          if (!songInfo) {
            const urlFilename = extractFilename(trimmedUrl);
            songInfo = currentWeekVideos.find(info => {
              const infoFilename = extractFilename(info.s3Url);
              const matches = urlFilename === infoFilename;
              if (matches) {
                console.log(`[IntroOutro] Found filename match in current week videos for ${slotId}: ${info.song} - ${info.artist}`);
              }
              return matches;
            });
          }
        }
        
        // If still not found, try partial filename match as last resort
        if (!songInfo) {
          const urlFilename = extractFilename(trimmedUrl);
          console.log(`[IntroOutro] Trying partial filename match for ${slotId}, filename: ${urlFilename}`);
          
          // Try all videos from master map first
          const allVideos = [...Array.from(masterVideoMap.values()), ...currentWeekVideos];
          
          songInfo = allVideos.find(info => {
            const infoFilename = extractFilename(info.s3Url);
            
            // Check if filenames share substantial part (at least 60% match)
            const longerName = urlFilename.length > infoFilename.length ? urlFilename : infoFilename;
            const shorterName = urlFilename.length <= infoFilename.length ? urlFilename : infoFilename;
            
            if (shorterName.length > 5 && longerName.includes(shorterName)) {
              console.log(`[IntroOutro] Found partial filename match for ${slotId}: ${info.song} - ${info.artist}`);
              return true;
            }
            
            return false;
          });
        }
        
        if (!songInfo) {
          console.log(`[IntroOutro] No song/artist info found for ${slotId}`);
          console.log(`[IntroOutro DEBUG] URL being searched: "${trimmedUrl}"`);
          console.log(`[IntroOutro DEBUG] Normalized URL: "${normalizedTrimmedUrl}"`);
          console.log(`[IntroOutro DEBUG] Filename: "${extractFilename(trimmedUrl)}"`);
          
          // Show some master video URLs for comparison
          const masterUrls = Array.from(masterVideoMap.values()).slice(0, 5);
          console.log(`[IntroOutro DEBUG] Sample master URLs:`, masterUrls.map(v => ({ song: v.song, url: v.s3Url, normalized: normalizeS3Url(v.s3Url) })));
          
          // Show current week video URLs for comparison
          if (currentWeekVideos.length > 0) {
            const currentUrls = currentWeekVideos.slice(0, 5);
            console.log(`[IntroOutro DEBUG] Sample current week URLs:`, currentUrls.map(v => ({ song: v.song, url: v.s3Url, normalized: normalizeS3Url(v.s3Url) })));
          }
          
          // If this is a problematic song, show more detailed comparison
          if (hasProblemSong) {
            console.log(`[IntroOutro DEBUG] DETAILED COMPARISON FOR PROBLEMATIC SONG:`);
            console.log(`[IntroOutro DEBUG] Searching URL: "${trimmedUrl}"`);
            console.log(`[IntroOutro DEBUG] Normalized searching URL: "${normalizedTrimmedUrl}"`);
            console.log(`[IntroOutro DEBUG] Searching filename: "${extractFilename(trimmedUrl)}"`);
            
            // Show all master video entries that might match
            const masterEntries = Array.from(masterVideoMap.values());
            masterEntries.forEach(entry => {
              const normalizedEntry = normalizeS3Url(entry.s3Url);
              const entryFilename = extractFilename(entry.s3Url);
              const urlMatch = normalizedEntry === normalizedTrimmedUrl;
              const filenameMatch = entryFilename === extractFilename(trimmedUrl);
              
              if (urlMatch || filenameMatch || problemSongs.some(problem => entry.song.toLowerCase().includes(problem))) {
                console.log(`[IntroOutro DEBUG] Master entry "${entry.song}": URL=${entry.s3Url}, normalized=${normalizedEntry}, filename=${entryFilename}, urlMatch=${urlMatch}, filenameMatch=${filenameMatch}`);
              }
            });
            
            // Show all current week entries that might match
            currentWeekVideos.forEach(entry => {
              const normalizedEntry = normalizeS3Url(entry.s3Url);
              const entryFilename = extractFilename(entry.s3Url);
              const urlMatch = normalizedEntry === normalizedTrimmedUrl;
              const filenameMatch = entryFilename === extractFilename(trimmedUrl);
              
              if (urlMatch || filenameMatch || problemSongs.some(problem => entry.song.toLowerCase().includes(problem))) {
                console.log(`[IntroOutro DEBUG] Current week entry "${entry.song}": URL=${entry.s3Url}, normalized=${normalizedEntry}, filename=${entryFilename}, urlMatch=${urlMatch}, filenameMatch=${filenameMatch}`);
              }
            });
          }
        }
      }

      const playerElement = (
        <Box sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}> {/* Added overflow hidden */}
          <MiniVideoPlayer
            key={`${slotId}-player-${currentFormat}`} // Include format in key to force remount
            slotId={slotId}
            s3Url={url}
            seekValue={seek}
            duration={slotId.startsWith('tease_shot') ? settings[`${slotId}_duration`] : undefined}
            previewTime={isPreviewSlot ? sliderValue : undefined} // Pass preview time only to selected slot when dragging
            onClick={handlePlayerClick}
            style={{ width: '100%', height: '100%' }} // Player fills the container
            song={songInfo?.song}
            artist={songInfo?.artist}
            currentFormat={currentFormat} // Pass current format for tease border selection
            horizontalPosition={currentFormat === 'vertical' ? (verticalVideoXPositions[slotId] || 0) : 0} // Only apply horizontal position in vertical format
            verticalPosition={currentFormat === 'vertical' ? (verticalVideoYPositions[slotId] || 0) : 0} // Only apply vertical position in vertical format
            isPreviewMode={showRedProgressBars && slotId.startsWith('tease_shot')} // Make progress bars red during preview only for tease shots
            teaseBorderUrl={teaseBorderUrl} // Dynamic tease border based on chart
          />
          {/* Place label inside the video at the top */}
          <Typography 
            variant="caption" 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              textAlign: 'center', 
              fontSize: '8px', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              padding: '1px'
            }}
          >
            {slotId}
          </Typography>
          {/* Tease border overlay is now rendered by MiniVideoPlayer */}
        </Box>
      );

      // Temporarily disabled draggable/resizable functionality due to React 18 compatibility
      // Just render static positioned videos for now
      players.push(
        <Box 
          key={slotId} 
          sx={{ 
            ...safeLayout, 
            position: 'absolute',
            padding: 0,
            margin: 0,
            overflow: 'hidden' // Ensure content fits exactly
          }}
        >
          {playerElement}
        </Box>
      );
    }
    return players;
  };


  // Dynamically generate slot IDs for the dropdown based on the active tab, with video_left first if applicable
  const allCurrentSlotIds = isTeaseActive
    ? ['tease_shot1', 'tease_shot2', 'tease_shot3', 'tease_shot4']
    : isIntroActive
    ? ['video_left', 'carousel_video1', 'carousel_video2', 'carousel_video3', 'carousel_video4']
    : (currentFormat === 'horizontal'
        ? ['outro_video1', 'outro_video2', 'outro_video3', 'outro_video4', 'outro_video5', 'outro_video6']
        : ['outro_video1', 'outro_video3', 'outro_video4', 'outro_video5', 'outro_video6']); // Vertical outro omits outro_video2

  // Filter to only show slots that have videos
  const currentSlotIds = allCurrentSlotIds.filter(slotId => {
    if (isTeaseActive) {
      return isSlotFilled(slotId);
    } else if (isIntroActive) {
      return !!(introSettings[`${slotId}_url`] && introSettings[`${slotId}_url`].trim() !== '');
    } else {
      return !!(outroSettings[`${slotId}_url`] && outroSettings[`${slotId}_url`].trim() !== '');
    }
  });


  // Check if the slider should be disabled - updated to not disable for video_left
  const isSliderDisabled = isUpdatingSheet || currentSlotIds.length === 0;
  
  // Debug logging for slider state
  console.log('🔍 [SLIDER DEBUG] Slider state:', { isUpdatingSheet, isSliderDisabled, isDraggingSlider });

  // Add Output Manager handler function
  const handleOutputManager = async () => {
    // Get the weekly sheet ID from the URL path
    const pathParts = location.pathname.split('/');
    const weeklySheetId = pathParts[pathParts.length - 1];
    
    if (!weeklySheetId) {
      enqueueSnackbar('No weekly sheet ID found', { variant: 'error' });
      return;
    }
    
    localStorage.setItem('currentSpreadsheetId', weeklySheetId);
    // Navigate with parentSpreadsheetId as query parameter
    navigate(`/output-manager?parentSpreadsheetId=${weeklySheetId}`);
  };

  return (
    <Box sx={{ p: 4 }}> {/* Outermost container for padding */}
      <Box sx={{ // Intermediate container for width control and centering
        width: currentFormat === 'vertical' ? '50%' : '100%',
        margin: currentFormat === 'vertical' ? '0 auto' : '0',
        // display: 'flex', // Removed flex properties
        // flexDirection: 'column', // Removed flex properties
      }}>
        {/* All content from here down is inside the new intermediate Box */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 1, 
          width: '100%' // Takes full width of the new intermediate parent
        }}>
          <Button
            variant="outlined"
            onClick={handleReturnToCharts}
            sx={{
              mr: 2,
              backgroundColor: '#ffffff',
              borderColor: '#000000',
              color: '#000000',
              '&:hover': {
                borderColor: '#000000',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Charts
          </Button>
          <Button
            variant="contained"
            onClick={handleOutputManager}
            size="small"
            sx={{
              height: '40px',
              padding: '4px 16px',
              display: 'flex',
              alignItems: 'center',
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: themeColor,
              color: '#000000',
              '&:hover': {
                backgroundColor: themeHoverColor,
                color: '#000000',
              },
            }}
          >
            OUTPUT MANAGER
          </Button>
        </Box>

        {/* Display cleanup notification if videos were removed */}
        {cleanupNotification && (
          <Alert severity="info" sx={{ mb: 2, width: '100%' }} onClose={() => setCleanupNotification(null)}>
            <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
              {cleanupNotification}
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, width: '100%' }}> {/* Ensure this Box also takes full width */}
          <ToggleButtonGroup
            value={currentFormat}
            exclusive
            onChange={handleFormatChange}
            aria-label="Video Format"
            sx={{
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: '#000000',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#333333',
                },
              },
            }}
          >
            <ToggleButton value="horizontal">Horizontal</ToggleButton>
            <ToggleButton value="vertical">Vertical</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          sx={{
            mb: 3,
            width: '100%',
            '& .MuiTab-root.Mui-selected': {
              color: '#000000',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#000000',
            },
          }}
        >
          {hasTeaseText && <Tab label="Tease" />}
          <Tab label="Intro" />
          <Tab label="Outro" />
        </Tabs>

        {/* Duration Display - Only show for Tease tab */}
        {isTeaseActive && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
              <Typography variant="body2">
                <strong>Tease VO Duration:</strong>{' '}
                {(teaseSettings.tease_intro_voiceover_url || processedAudioUrl) ?
                  (teaseVoDuration !== null ? `${teaseVoDuration.toFixed(1)}s` : 'Calculating...')
                  : 'No tease VO'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: (() => {
                    const total = ['tease_shot1', 'tease_shot2', 'tease_shot3', 'tease_shot4']
                      .reduce((sum, shot) => {
                        if (teaseSettings[`${shot}_url`]) {
                          const duration = parseFloat(teaseSettings[`${shot}_duration`] || '0');
                          return sum + duration;
                        }
                        return sum;
                      }, 0);
                    return total > 10 ? 'error.main' : 'text.primary';
                  })()
                }}
              >
                <strong>Total Shot(s) Duration:</strong>
                {(() => {
                  const total = ['tease_shot1', 'tease_shot2', 'tease_shot3', 'tease_shot4']
                    .reduce((sum, shot) => {
                      if (teaseSettings[`${shot}_url`]) {
                        const duration = parseFloat(teaseSettings[`${shot}_duration`] || '0');
                        return sum + duration;
                      }
                      return sum;
                    }, 0);
                  const effective = teaseVoDuration !== null && total < teaseVoDuration ? teaseVoDuration : total;
                  const wasExtended = teaseVoDuration !== null && total < teaseVoDuration;
                  return wasExtended
                    ? ` ${total.toFixed(1)}s → ${effective.toFixed(1)}s (last shot extended to cover VO)`
                    : ` ${total.toFixed(1)}s`;
                })()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2">
                  <strong>VO Gain:</strong>{' '}
                </Typography>
                {isEditingTeaseGain ? (
                  <input
                    type="text"
                    autoFocus
                    value={teaseGainInputValue}
                    onChange={(e) => setTeaseGainInputValue(e.target.value)}
                    onBlur={() => {
                      saveTeaseGainOverride(teaseGainInputValue);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        saveTeaseGainOverride(teaseGainInputValue);
                      } else if (e.key === 'Escape') {
                        setIsEditingTeaseGain(false);
                      }
                    }}
                    style={{
                      width: '50px',
                      padding: '2px 4px',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}
                    placeholder={globalGain}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    onClick={() => {
                      setTeaseGainInputValue(teaseGainOverride || '');
                      setIsEditingTeaseGain(true);
                    }}
                    sx={{
                      cursor: 'pointer',
                      color: teaseGainOverride ? 'text.primary' : 'text.secondary',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    title={teaseGainOverride ? 'Click to edit override gain' : `Click to set override (using global: ${globalGain}dB)`}
                  >
                    {teaseGainOverride ? `${teaseGainOverride}dB` : `Global (${globalGain}dB)`}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Background and Player Container */}
        <Box 
          sx={{
            position: 'relative',
            width: '100%', // Takes full width of its new intermediate parent
            paddingTop: currentFormat === 'horizontal' ? '56.25%' : '177.78%', // Dynamic aspect ratio
            backgroundColor: 'rgba(0,0,0,0.1)',
            overflow: 'hidden',
            border: '1px solid #ccc',
          }}
        >
          {/* Background image in its own layer */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: isTeaseActive ? 'none' : `url(${
                isIntroActive ? (currentFormat === 'horizontal' ? '/intro.jpg' : '/v-intro.jpg') 
                : (currentFormat === 'horizontal' ? '/outro.jpg' : '/v-outro.jpg')
              })`,
              backgroundColor: isTeaseActive ? '#f5f5f5' : 'transparent',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              zIndex: 0
            }}
          />
          
          {/* Content layer */}
          <Box
            ref={playerAreaRef} // Assign ref here
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1
            }}
          >
            {/* Render Players based on active tab using DEFAULT_LAYOUT */}
            {isTeaseActive && renderPlayers(teaseSettings, 'tease_shot', 4)}
            {isIntroActive && renderPlayers(introSettings, 'carousel', 4)}
            {isIntroActive && renderPlayers(introSettings, 'video_left', 1, true)}
            {isOutroActive && renderPlayers(outroSettings, 'outro', 6)} 
          </Box>
        </Box>
      </Box> {/* End width-constrained section - controls below use full width */}

        {/* Centered Action Buttons */}
        {isTeaseActive && (
          <Box sx={{
            mt: 2,
            mb: 2,
            display: 'flex',
            justifyContent: 'center',
            gap: 2
          }}>
            <Button
              data-testid="generate-tease-button"
              variant="contained"
              disabled={isGeneratingTeaseMovie}
              sx={{
                backgroundColor: '#000000',
                color: themeColor,
                '&:hover': {
                  backgroundColor: '#333333',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
              onClick={async () => {
                try {
                  setIsGeneratingTeaseMovie(true);
                  console.log('[Generate Tease] Starting tease generation...');

                  // STEP 1: Generate tease.wav first
                  enqueueSnackbar('Step 1: Generating tease audio...', { variant: 'info' });

                  // Fetch VO data (tease text, line VO, line gain) + week settings (global gain)
                  const [genVo, genSettings] = await Promise.all([
                    chartDataService.getVO(weeklySheetId!, getVoLanguage(weeklySheetId)),
                    chartDataService.getSettings(weeklySheetId!),
                  ]);

                  // Get tease text (was L15) = the 'Tease' section script text
                  const teaseText = genVo.voData?.['Tease'] || '';

                  if (!teaseText.trim()) {
                    enqueueSnackbar('No tease text found', { variant: 'error' });
                    return;
                  }

                  // Check for line-specific tease VO (was S15)
                  const lineVoUrl = genVo.lineVoUrls?.['Tease'] || '';

                  // Get tease timing (prefers Line VO In/Out override, falls back to main VO In/Out)
                  const teaseTiming = await fetchTeaseTiming(0, 5);
                  const voIn = teaseTiming.voIn;
                  const voOut = teaseTiming.voOut;
                  console.log('[Generate Tease] Tease timing source:', teaseTiming.source);

                  // Prefer line-specific tease VO, otherwise use main processed audio
                  let audioUrlToUse = processedAudioUrl;
                  let usingLineSpecificVo = false;
                  if (lineVoUrl && lineVoUrl.trim()) {
                    console.log('[Generate Tease] Using line-specific tease VO:', lineVoUrl);
                    audioUrlToUse = lineVoUrl.trim();
                    usingLineSpecificVo = true;
                  } else {
                    console.log('[Generate Tease] Using main processed VO');
                  }

                  if (!audioUrlToUse) {
                    enqueueSnackbar('No audio source found (no line VO or processed VO)', { variant: 'error' });
                    return;
                  }

                  // Get audio settings (gain)
                  // ALWAYS check the tease line-specific gain first, then fall back to the
                  // week's global VO gain. Applies regardless of which VO source is used.
                  let gain = '0';
                  let gainSource = 'default';

                  // Try line-specific tease gain (was P15) first
                  const teaseLineGain = genVo.lineGain?.['Tease'];
                  const lineGain = (teaseLineGain !== undefined && teaseLineGain !== null) ? String(teaseLineGain) : '';

                  if (lineGain && lineGain.toString().trim() !== '') {
                    // line gain has a value, use it
                    gain = lineGain.toString().trim();
                    gainSource = 'tease line gain';
                  } else {
                    // line gain is blank, fall back to the week global VO gain (was P14)
                    const p14Gain = genSettings.globalGain;

                    if (p14Gain && p14Gain.toString().trim() !== '') {
                      gain = p14Gain.toString().trim();
                      gainSource = 'global gain';
                    } else {
                      // global gain is also empty, fall back to localStorage
                      const localStorageGain = localStorage.getItem('limiterSettings_gain');
                      if (localStorageGain && !isNaN(parseFloat(localStorageGain))) {
                        gain = localStorageGain;
                        gainSource = 'localStorage (global gain was empty)';
                        console.log(`[Generate Tease] global gain is empty, using localStorage gain: ${gain}dB`);
                      } else {
                        gain = '0';
                        gainSource = 'default (no gain found anywhere)';
                      }
                    }
                  }

                  console.log(`[Generate Tease] Using gain: ${gain}dB from ${gainSource} (usingLineSpecificVo: ${usingLineSpecificVo})`);

                  // Format the date for S3 folder
                  const formattedDate = formatSheetDate(currentDateString);

                  console.log('[Generate Tease] Generating tease.wav with params:', {
                    text: teaseText,
                    voIn,
                    voOut,
                    gain,
                    gainSource,
                    date: formattedDate,
                    audioUrl: audioUrlToUse,
                    usingLineSpecificVo
                  });

                  // Call the export-vo-files endpoint to generate tease.wav
                  const voResponse = await fetch(`${SERVER_BASE_URL}/api/export-vo-files`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      spreadsheetId: weeklySheetId,
                      sheetDate: formattedDate,
                      voData: [{
                        key: 'Tease',
                        text: teaseText,
                        inPoint: voIn,
                        outPoint: voOut,
                        audioUrl: audioUrlToUse,
                        gain: gain || '0'
                      }],
                      audioSettings: {},
                      voSubfolder: currentChartConfig?.voSubfolder || '',
                      language: activeVoLang
                    })
                  });

                  if (!voResponse.ok) {
                    const errorData = await voResponse.json();
                    throw new Error(errorData.error || 'Failed to generate tease VO');
                  }

                  const voResult = await voResponse.json();
                  console.log('[Generate Tease] Tease.wav generated successfully:', voResult);

                  // Update B63 with the generated tease.wav URL (language-scoped key;
                  // URL must mirror the server's buildVoS3Prefix language segment)
                  const genVoSubfolder = currentChartConfig?.voSubfolder || '';
                  const genLangSeg = activeVoLang !== 'en' ? `${activeVoLang}/` : '';
                  const teaseVoUrl = `https://c1-scott-scratchdisk.s3.amazonaws.com/Billboard/VO/${genVoSubfolder ? genVoSubfolder + '/' : ''}${genLangSeg}${formattedDate}/tease.wav`;
                  await chartDataService.setSetting(weeklySheetId, teaseSettingKey('tease_intro_voiceover_url', activeVoLang), teaseVoUrl);
                  console.log('[Generate Tease] Updated tease_intro_voiceover_url with tease.wav URL:', teaseVoUrl);

                  // Clear the cached tease VO before generating movie to ensure fresh audio is used
                  console.log('[Generate Tease] Clearing cached tease VO...');
                  try {
                    const clearCacheResponse = await fetch(`${SERVER_BASE_URL}/api/clear-tease-vo-cache`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ teaseVoUrl })
                    });
                    const clearResult = await clearCacheResponse.json();
                    console.log('[Generate Tease] Cache clear result:', clearResult);
                  } catch (cacheError) {
                    console.warn('[Generate Tease] Failed to clear cache (continuing anyway):', cacheError);
                  }

                  // STEP 2: Now generate the tease movie using the freshly generated tease.wav
                  enqueueSnackbar('Step 2: Generating tease movie...', { variant: 'info' });

                  // Validate total duration (only count configured shots)
                  const totalDuration = ['tease_shot1', 'tease_shot2', 'tease_shot3', 'tease_shot4']
                    .reduce((sum, shot) => {
                      if (teaseSettings[`${shot}_url`]) {
                        const duration = parseFloat(teaseSettings[`${shot}_duration`] || '0');
                        return sum + duration;
                      }
                      return sum;
                    }, 0);

                  if (totalDuration > 10) {
                    enqueueSnackbar(`Total shot duration (${totalDuration}s) exceeds 10 second limit`, { variant: 'error' });
                    return;
                  }

                  // Prepare shots data - read fresh settings to get latest slider values
                  console.log('[Generate Tease] Reading fresh tease settings...');
                  const freshSettingsResp = await chartDataService.getSettings(weeklySheetId);
                  const freshSettings = freshSettingsResp.settings || {};

                  // Helper to get a fresh tease setting value by key
                  const getFreshValue = (key: string): string => {
                    const value = freshSettings[key];
                    return (value === undefined || value === null) ? '' : String(value);
                  };

                  const shots = ['tease_shot1', 'tease_shot2', 'tease_shot3', 'tease_shot4']
                    .map((shotId) => {
                      // Each shot has 5 settings keys: url, seek, duration, h_offset, v_offset
                      const url = getFreshValue(`${shotId}_url`);
                      const seek = getFreshValue(`${shotId}_seek`);
                      const duration = getFreshValue(`${shotId}_duration`);
                      const h_offset_raw = getFreshValue(`${shotId}_h_offset`);
                      const v_offset_raw = getFreshValue(`${shotId}_v_offset`);

                      console.log(`[Generate Tease] Shot ${shotId}: h_offset_raw="${h_offset_raw}", v_offset_raw="${v_offset_raw}" (fresh settings)`);
                      return {
                        url: url,
                        seek: seek || '0',
                        duration: duration || '0',
                        sourceType: 'youtube', // Default to youtube
                        horizontalPosition: parseFloat(h_offset_raw || '0'),
                        verticalPosition: parseFloat(v_offset_raw || '0')
                      };
                    })
                    .filter(shot => shot.url && parseFloat(shot.duration) > 0);

                  console.log('[Generate Tease] Final shots array being sent to backend:', JSON.stringify(shots, null, 2));

                  if (shots.length === 0) {
                    enqueueSnackbar('No valid shots configured', { variant: 'error' });
                    return;
                  }

                  const requestData = {
                    shots,
                    teaseVoUrl,
                    weeklySheetId,
                    chartId: currentChartConfig?.chartId || '',
                    // Add folder organization parameters for S3 path
                    voSubfolder: currentChartConfig?.voSubfolder || '',
                    sheetDate: formatSheetDate(currentDateString),
                    // Language scopes the tease movie's S3 path so EN/ES don't overwrite each other
                    language: activeVoLang
                    // Note: voGain is NOT passed here - gain is already baked into tease.wav from export-vo-files
                  };

                  console.log('Sending generate request:', requestData);

                  const response = await fetch('/api/generate-tease-movie', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestData)
                  });

                  if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.details || errorData.error || 'Generation failed');
                  }

                  const result = await response.json();
                  console.log('Generation successful:', result);

                  // Persist the generated tease movie URLs (tease_horizontal/tease_vertical
                  // settings, language-scoped so each language keeps its own tease movie)
                  try {
                    await Promise.all([
                      chartDataService.setSetting(weeklySheetId, teaseSettingKey('tease_horizontal', activeVoLang), result.results.horizontal),
                      chartDataService.setSetting(weeklySheetId, teaseSettingKey('tease_vertical', activeVoLang), result.results.vertical),
                    ]);
                    console.log('[IntroOutro] Persisted generated tease URLs');
                  } catch (sheetError) {
                    console.error('[IntroOutro] Error saving tease URLs:', sheetError);
                    enqueueSnackbar('Warning: Failed to save generated tease URLs', { variant: 'warning' });
                  }

                  enqueueSnackbar('Tease movie generated successfully!', { variant: 'success' });

                  // Update the generated tease player section with the new URLs
                  // Append cache-busting param since S3 key is fixed per chart/date
                  const cacheBust = `?t=${Date.now()}`;
                  setGeneratedTeaseUrls({
                    vertical: result.results.vertical + cacheBust,
                    horizontal: result.results.horizontal + cacheBust
                  });

                } catch (error) {
                  console.error('[Generate Tease] Error:', error);
                  const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                  enqueueSnackbar(`Generation failed: ${errorMessage}`, { variant: 'error' });
                } finally {
                  setIsGeneratingTeaseMovie(false);
                }
              }}
            >
              Generate Tease
            </Button>
          </Box>
        )}

        {/* Helper text for VO button */}
        {isTeaseActive && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            {!processedAudioUrl && (
              <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                Upload a VO file in VO Manager to enable the Refresh Tease VO button
              </Typography>
            )}
            {!hasTeaseText && processedAudioUrl && (
              <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                Add text in the Voice Over Text column (L15) to enable the Refresh Tease VO button
              </Typography>
            )}
          </Box>
        )}

        {/* Seek Time Adjustment Controls */}
        <Box sx={{ 
          mt: 3, 
          p: 2, 
          border: '1px solid #ddd', 
          borderRadius: '4px', 
          width: '100%' // Takes full width of its new intermediate parent
        }}>
          <Typography variant="h6" gutterBottom>
            {isTeaseActive ? 'Adjust Tease Shot Timing' : 'Adjust Seek In Time'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Duration field for tease shots - first column */}
            {isTeaseActive && selectedSlotForSlider && selectedSlotForSlider.startsWith('tease_shot') && (
              <Box sx={{ flexBasis: '15%', minWidth: '80px' }}>
                <TextField
                  label="Duration"
                  type="number"
                  size="small"
                  fullWidth
                  value={teaseSettings[`${selectedSlotForSlider}_duration`] || '5'}
                  onChange={async (e) => {
                    const newDuration = e.target.value;
                    setTeaseSettings(prev => ({
                      ...prev,
                      [`${selectedSlotForSlider}_duration`]: newDuration
                    }));

                    // Persist immediately
                    try {
                      const keys = getKeysForSlot(selectedSlotForSlider, activeVoLang);
                      if (keys?.durationKey) {
                        await chartDataService.setSetting(weeklySheetId, keys.durationKey, newDuration);
                        console.log(`[IntroOutro] Synced ${selectedSlotForSlider} duration to ${keys.durationKey}: ${newDuration}`);
                      }
                    } catch (error) {
                      console.error(`[IntroOutro] Error syncing ${selectedSlotForSlider} duration:`, error);
                    }
                  }}
                  onBlur={async () => {
                    // Save duration
                    const keys = getKeysForSlot(selectedSlotForSlider, activeVoLang);
                    if (keys?.durationKey && weeklySheetId) {
                      try {
                        setIsUpdatingSheet(true);
                        await chartDataService.setSetting(weeklySheetId, keys.durationKey, teaseSettings[`${selectedSlotForSlider}_duration`] || '0');
                      } catch (error) {
                        console.error('Failed to save duration:', error);
                      } finally {
                        setIsUpdatingSheet(false);
                      }
                    }
                  }}
                />
              </Box>
            )}
            <Box sx={{ flexBasis: isTeaseActive && selectedSlotForSlider && selectedSlotForSlider.startsWith('tease_shot') ? '25%' : '30%' }}>
              <FormControl fullWidth size="small">
                <InputLabel id="select-slot-label" shrink={Boolean(selectedSlotForSlider)}>Select Video Slot</InputLabel>
                <Select
                  labelId="select-slot-label"
                  id="select-slot"
                  value={currentSlotIds.length > 0 ? selectedSlotForSlider : ''}
                  label="Select Video Slot"
                  onChange={handleSlotSelectionChange}
                  disabled={currentSlotIds.length === 0}
                  displayEmpty
                >
                  {currentSlotIds.length === 0 ? (
                    <MenuItem value="" disabled>
                      No videos available
                    </MenuItem>
                  ) : (
                    currentSlotIds.map(slotId => (
                      <MenuItem key={slotId} value={slotId}>
                        {(() => {
                          if (slotId.startsWith('tease_shot')) {
                            const shotNumber = slotId.replace('tease_shot', '');
                            return `Tease Shot ${shotNumber}`;
                          }
                          return slotId.replace(/_/g, ' ').replace(/video/g, 'Video');
                        })()}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flexBasis: isTeaseActive && selectedSlotForSlider && selectedSlotForSlider.startsWith('tease_shot') ? '60%' : '65%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Slider
                  value={sliderValue}
                  onChange={handleSliderChange}
                  onChangeCommitted={(event, value) => {
                    console.log('🚨 [INLINE DEBUG] onChangeCommitted fired!', { value });
                    handleSliderChangeCommitted(event, value);
                  }}
                  onMouseUp={() => {
                    console.log('🚨 [MOUSE UP] Mouse up on slider, value:', sliderValue);
                    handleSliderChangeCommitted({} as React.SyntheticEvent, sliderValue);
                  }}
                  onTouchEnd={() => {
                    console.log('🚨 [TOUCH END] Touch end on slider, value:', sliderValue);
                    handleSliderChangeCommitted({} as React.SyntheticEvent, sliderValue);
                  }}
                  aria-labelledby="seek-slider"
                  valueLabelDisplay="auto"
                  step={0.01}
                  min={0}
                  max={videoDuration}
                  disabled={isSliderDisabled}
                  sx={{
                    mr: 2,
                    color: '#000000',
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#000000',
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: '#000000',
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: '#9e9e9e',
                    },
                  }}
                />
                {isSliderDisabled ? (
                  <Typography variant="body2" sx={{ minWidth: '40px', textAlign: 'right' }}>
                    N/A
                  </Typography>
                ) : (
                  <TextField
                    value={timeInputValue}
                    onChange={handleTimeInputChange}
                    onBlur={handleTimeInputBlur}
                    onKeyDown={handleTimeInputKeyDown}
                    disabled={isUpdatingSheet}
                    size="small"
                    sx={{ 
                      width: '150px',
                      '& input': { 
                        textAlign: 'right',
                        padding: '4px 8px'
                      }
                    }}
                    InputProps={{
                      endAdornment: <Typography variant="body2" sx={{ ml: 0.5 }}>s / {videoDuration}s</Typography>
                    }}
                  />
                )}
                {isUpdatingSheet && <CircularProgress size={20} sx={{ ml: 1 }} />} 
              </Box>
            </Box>
          </Box>

          {/* Horizontal Position Control for Vertical Format */}
          {currentFormat === 'vertical' && selectedSlotForSlider && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Horizontal Position (for centering video)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={verticalVideoXPositions[selectedSlotForSlider] || 0}
                  onChange={(e, newValue) => {
                    const position = newValue as number;
                    handleHorizontalPositionChange(position);
                  }}
                  min={-50}
                  max={50}
                  marks={[
                    { value: -50, label: 'Left' },
                    { value: 0, label: 'Center' },
                    { value: 50, label: 'Right' }
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value > 0 ? '+' : ''}${value}%`}
                  sx={{
                    flex: 1,
                    mr: 2,
                    color: '#000000',
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#000000',
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: '#000000',
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: '#9e9e9e',
                    },
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setVerticalVideoXPositions(prev => ({
                      ...prev,
                      [selectedSlotForSlider]: 0
                    }));
                    // Save to Google Sheets
                    saveHorizontalPositionToSheet(selectedSlotForSlider, 0);
                  }}
                  sx={{ minWidth: '80px' }}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          )}

          {/* Vertical Position Control for Vertical Format */}
          {currentFormat === 'vertical' && selectedSlotForSlider && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Vertical Position (for centering video)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={verticalVideoYPositions[selectedSlotForSlider] || 0}
                  onChange={(e, newValue) => {
                    const position = newValue as number;
                    console.log(`[VerticalSlider] ${selectedSlotForSlider} vertical position changed to:`, position);
                    handleVerticalPositionChange(position);
                  }}
                  min={-50}
                  max={50}
                  marks={[
                    { value: -50, label: 'Top' },
                    { value: 0, label: 'Center' },
                    { value: 50, label: 'Bottom' }
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value > 0 ? '+' : ''}${value}%`}
                  sx={{
                    flex: 1,
                    mr: 2,
                    color: '#000000',
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#000000',
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: '#000000',
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: '#9e9e9e',
                    },
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setVerticalVideoYPositions(prev => ({
                      ...prev,
                      [selectedSlotForSlider]: 0
                    }));
                    // Save to Google Sheets
                    saveVerticalPositionToSheet(selectedSlotForSlider, 0);
                  }}
                  sx={{ minWidth: '80px' }}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          )}


          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>

        {/* Tease Movie Configuration */}
        {isTeaseActive && (
          <Box sx={{
            mt: 3,
            p: 2,
            border: '1px solid #ddd',
            borderRadius: '4px',
            width: '100%'
          }}>



            {/* Shot Configuration Grid */}
            <Box sx={{ mb: 3 }}>
              {['tease_shot1', 'tease_shot2', 'tease_shot3', 'tease_shot4'].map((shotId, index) => (
                <Box key={shotId} sx={{
                  mb: 2,
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  backgroundColor: selectedSlotForSlider === shotId ? '#e3f2fd' : 'transparent'
                }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Shot {index + 1}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    {/* Source Selection */}
                    <Box sx={{ flexBasis: { xs: '100%', sm: '25%' } }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Source</InputLabel>
                        <Select
                          value={teaseSettings[`${shotId}_source_type`] || (isSlotFilled(shotId) ? 'youtube' : 'placeholder')}
                          label="Source"
                          disabled={!canFillSlot(shotId) && !isSlotFilled(shotId)}
                          onChange={(e) => {
                            if (e.target.value !== 'placeholder') {
                              setTeaseSettings(prev => ({
                                ...prev,
                                [`${shotId}_source_type`]: e.target.value
                              }));
                            }
                          }}
                        >
                          {!isSlotFilled(shotId) && (
                            <MenuItem value="placeholder" disabled>
                              Add Video
                            </MenuItem>
                          )}
                          <MenuItem value="youtube">
                            Chart Video
                          </MenuItem>
                          <MenuItem value="upload">
                            {teaseSettings[`${shotId}_source_type`] === 'upload' ? 'Uploaded Video' : 'Upload Video'}
                          </MenuItem>
                          <MenuItem value="library">
                            Library Video
                          </MenuItem>
                          <MenuItem value="youtube_import">
                            Add from YouTube
                          </MenuItem>
                          {isSlotFilled(shotId) && (
                            <MenuItem
                              value="remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeVideoFromSlot(shotId);
                                // Reset to default after removal
                                setTeaseSettings(prev => ({
                                  ...prev,
                                  [`${shotId}_source_type`]: 'youtube'
                                }));
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              Remove Video
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Video Selection/Upload */}
                    <Box sx={{ flexBasis: { xs: '100%', sm: '35%' } }}>
                      {teaseSettings[`${shotId}_source_type`] === 'upload' ? (
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          size="small"
                          disabled={!canFillSlot(shotId) && !isSlotFilled(shotId)}
                        >
                          {teaseSettings[`${shotId}_url`] ? 'Change Video' : 'Upload Video'}
                          <input
                            type="file"
                            hidden
                            accept="video/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;

                              try {
                                console.log('Uploading custom video:', file.name);

                                const formData = new FormData();
                                formData.append('video', file);
                                // Add folder organization parameters for S3 path
                                if (currentChartConfig?.voSubfolder) {
                                  formData.append('voSubfolder', currentChartConfig.voSubfolder);
                                }
                                if (currentDateString) {
                                  formData.append('sheetDate', formatSheetDate(currentDateString));
                                }

                                const response = await fetch('/api/upload-tease-video', {
                                  method: 'POST',
                                  body: formData
                                });

                                if (!response.ok) {
                                  throw new Error(`Upload failed: ${response.statusText}`);
                                }

                                const result = await response.json();
                                console.log('Upload successful:', result);

                                // Persist the new URL to the slot's settings key
                                if (weeklySheetId) {
                                  try {
                                    const keys = getKeysForSlot(shotId, activeVoLang);
                                    console.log(`[IntroOutro] Settings key mapping for ${shotId}:`, keys);
                                    if (keys?.urlKey) {
                                      await chartDataService.setSetting(weeklySheetId, keys.urlKey, result.url);
                                      console.log(`[IntroOutro] Updated ${keys.urlKey} with uploaded video URL: ${result.url}`);
                                    } else {
                                      console.error(`[IntroOutro] No key mapping found for ${shotId}`);
                                    }
                                  } catch (sheetError) {
                                    console.error(`[IntroOutro] Error saving uploaded URL:`, sheetError);
                                    enqueueSnackbar('Warning: Video uploaded but failed to save', { variant: 'warning' });
                                  }
                                } else {
                                  console.warn('[IntroOutro] No weeklySheetId available, skipping save');
                                  enqueueSnackbar('Warning: Video uploaded but no sheet ID available for saving', { variant: 'warning' });
                                }

                                // Update the tease settings with the new URL
                                setTeaseSettings(prev => ({
                                  ...prev,
                                  [`${shotId}_url`]: result.url,
                                  [`${shotId}_custom_filename`]: result.originalName,
                                  [`${shotId}_source_type`]: 'upload'
                                }));

                                enqueueSnackbar(`Successfully uploaded ${result.originalName}`, { variant: 'success' });
                              } catch (error) {
                                console.error('Upload error:', error);
                                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                                enqueueSnackbar(`Upload failed: ${errorMessage}`, { variant: 'error' });
                              }

                              // Clear the input
                              e.target.value = '';
                            }}
                          />
                        </Button>
                      ) : teaseSettings[`${shotId}_source_type`] === 'library' ? (
                        <Autocomplete
                          size="small"
                          fullWidth
                          options={Array.from(masterVideoMap.values())}
                          getOptionLabel={(option) => `${option.artist} - ${option.song}`}
                          filterOptions={(options, { inputValue }) => {
                            const searchTerm = inputValue.toLowerCase();
                            return options.filter(option =>
                              option.artist.toLowerCase().includes(searchTerm) ||
                              option.song.toLowerCase().includes(searchTerm)
                            );
                          }}
                          value={
                            teaseSettings[`${shotId}_url`]
                              ? Array.from(masterVideoMap.values()).find(v => v.s3Url === teaseSettings[`${shotId}_url`]) || null
                              : null
                          }
                          onChange={async (event, newValue) => {
                            if (newValue) {
                              console.log(`[IntroOutro] Library video selected for ${shotId}:`, newValue);

                              // Get seek time from master video
                              const seekValue = newValue.seekIn || '0';

                              // Persist URL (+ seek) to the slot's settings keys
                              if (weeklySheetId) {
                                try {
                                  const keys = getKeysForSlot(shotId, activeVoLang);
                                  if (keys?.urlKey) {
                                    const writes: Promise<void>[] = [
                                      chartDataService.setSetting(weeklySheetId, keys.urlKey, newValue.s3Url)
                                    ];
                                    if (keys.seekKey) {
                                      writes.push(chartDataService.setSetting(weeklySheetId, keys.seekKey, seekValue));
                                    }
                                    await Promise.all(writes);
                                    console.log(`[IntroOutro] Persisted settings for ${shotId} with library video`);
                                  }
                                } catch (sheetError) {
                                  console.error(`[IntroOutro] Error saving library video:`, sheetError);
                                  enqueueSnackbar('Warning: Failed to save library video', { variant: 'warning' });
                                }
                              }

                              // Update tease settings
                              setTeaseSettings(prev => ({
                                ...prev,
                                [`${shotId}_url`]: newValue.s3Url,
                                [`${shotId}_seek`]: seekValue,
                                [`${shotId}_source_type`]: 'library'
                              }));

                              enqueueSnackbar(`Added "${newValue.song}" by ${newValue.artist}`, { variant: 'success' });
                            }
                          }}
                          disabled={!canFillSlot(shotId) && !isSlotFilled(shotId)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Search library"
                              placeholder="Type artist or song..."
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={option.s3Url}>
                              <Box>
                                <Typography variant="body2" fontWeight="bold">{option.song}</Typography>
                                <Typography variant="caption" color="text.secondary">{option.artist}</Typography>
                              </Box>
                            </li>
                          )}
                        />
                      ) : teaseSettings[`${shotId}_source_type`] === 'youtube_import' ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Paste YouTube URL..."
                            value={youtubeImportUrl[shotId] || ''}
                            onChange={(e) => setYoutubeImportUrl(prev => ({ ...prev, [shotId]: e.target.value }))}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            disabled={!youtubeImportUrl[shotId]}
                            onClick={() => handleYoutubeImport(shotId, youtubeImportUrl[shotId])}
                          >
                            Go
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          variant="outlined"
                          fullWidth
                          size="small"
                          disabled={
                            (!canFillSlot(shotId) && !isSlotFilled(shotId)) ||
                            (!isSlotFilled(shotId) && teaseSettings[`${shotId}_source_type`] !== 'youtube')
                          }
                          onClick={() => {
                            setSelectedSlot(shotId);
                            setDialogOpen(true);
                          }}
                        >
                          {teaseSettings[`${shotId}_url`] ? 'Change Video' : 'Add Video'}
                        </Button>
                      )}
                    </Box>

                    {/* In Point */}
                    <Box sx={{ flexBasis: { xs: '100%', sm: '15%' } }}>
                      <TextField
                        label="In Point (s)"
                        type="number"
                        size="small"
                        fullWidth
                        disabled={!isSlotFilled(shotId)}
                        value={teaseSettings[`${shotId}_seek`] || '0'}
                        onChange={async (e) => {
                          const newSeekValue = e.target.value;
                          setTeaseSettings(prev => ({
                            ...prev,
                            [`${shotId}_seek`]: newSeekValue
                          }));

                          // Persist immediately
                          try {
                            const keys = getKeysForSlot(shotId, activeVoLang);
                            if (keys?.seekKey) {
                              await chartDataService.setSetting(weeklySheetId, keys.seekKey, newSeekValue);
                              console.log(`[IntroOutro] Synced ${shotId} seek time to ${keys.seekKey}: ${newSeekValue}`);
                            }
                          } catch (error) {
                            console.error(`[IntroOutro] Error syncing ${shotId} seek time:`, error);
                          }
                        }}
                        inputProps={{ min: 0, step: 0.1 }}
                      />
                    </Box>

                    {/* Duration */}
                    <Box sx={{ flexBasis: { xs: '100%', sm: '15%' } }}>
                      <TextField
                        label="Duration (s)"
                        type="number"
                        size="small"
                        fullWidth
                        disabled={!isSlotFilled(shotId)}
                        value={teaseSettings[`${shotId}_duration`] || '2.5'}
                        onChange={async (e) => {
                          const newDuration = e.target.value;
                          setTeaseSettings(prev => ({
                            ...prev,
                            [`${shotId}_duration`]: newDuration
                          }));

                          // Persist immediately
                          try {
                            const keys = getKeysForSlot(shotId, activeVoLang);
                            if (keys?.durationKey) {
                              await chartDataService.setSetting(weeklySheetId, keys.durationKey, newDuration);
                              console.log(`[IntroOutro] Synced ${shotId} duration to ${keys.durationKey}: ${newDuration}`);
                            }
                          } catch (error) {
                            console.error(`[IntroOutro] Error syncing ${shotId} duration:`, error);
                          }
                        }}
                        onBlur={async () => {
                          // Save duration
                          const keys = getKeysForSlot(shotId, activeVoLang);
                          if (keys?.durationKey && weeklySheetId) {
                            try {
                              setIsUpdatingSheet(true);
                              await chartDataService.setSetting(weeklySheetId, keys.durationKey, teaseSettings[`${shotId}_duration`] || '0');
                              console.log(`[Tease] Saved duration for ${shotId}: ${teaseSettings[`${shotId}_duration`]}`);
                            } catch (error) {
                              console.error(`Failed to save duration for ${shotId}:`, error);
                            } finally {
                              setIsUpdatingSheet(false);
                            }
                          }
                        }}
                        inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                      />
                    </Box>
                  </Box>

                </Box>
              ))}
            </Box>


            {/* Generated Tease Player */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Generated Tease Movies
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom>Vertical (9:16)</Typography>
                  {generatedTeaseUrls.vertical ? (
                    <Box sx={{ aspect: '9/16', border: '1px solid #ccc' }}>
                      <video
                        key={generatedTeaseUrls.vertical}
                        controls
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        src={generatedTeaseUrls.vertical}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </Box>
                  ) : (
                    <Box sx={{
                      aspect: '9/16',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px dashed #ccc'
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        Generate to preview
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom>Horizontal (16:9)</Typography>
                  {generatedTeaseUrls.horizontal ? (
                    <Box sx={{ aspect: '16/9', border: '1px solid #ccc' }}>
                      <video
                        key={generatedTeaseUrls.horizontal}
                        controls
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        src={generatedTeaseUrls.horizontal}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </Box>
                  ) : (
                    <Box sx={{
                      aspect: '16/9',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px dashed #ccc'
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        Generate to preview
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Video Selection Dialog */}
        {dialogOpen && (
          <VideoSelectionDialog
            open={dialogOpen}
            onClose={handleDialogClose}
            masterVideoList={currentWeekVideos.length > 0 ? currentWeekVideos : Array.from(masterVideoMap.values())}
            currentSlotId={selectedSlot}
            onSave={handleSaveVideoSelection}
            activeTab={activeTab}
            introSettings={introSettings as any}
            outroSettings={outroSettings as any}
            teaseSettings={teaseSettings as any}
            hasTeaseText={hasTeaseText}
          />
        )}

    </Box>
  );
};

export default IntroOutroPage;